/**
 * What is known about a symbol, as one list of views.
 *
 * **Three hosts, one registry.** The PIP renders a view in a 170px corner, the
 * data panel renders the same view full size in a tab, and a detached window
 * will render it again on another monitor. Written once here, so a new view —
 * an options chain, filings, earnings — is one entry and appears in all of them
 * at once. Three parallel lists is the failure this is built to avoid; they
 * drift the first time one of them gains a field.
 *
 * ## A view says how small it can get
 *
 * `compact` is the PIP's form, not a scale factor: Profile drops to six rows
 * with shorter labels, and a table shows four rows rather than twenty. A panel
 * shrunk by CSS produces six-point type and a horizontal scrollbar, which is how
 * "it works at both sizes" usually turns out to be false.
 *
 * ## A view with no source says so
 *
 * Five of the six had nothing to fetch from when this file was written, and each
 * rendered an empty state **naming what is missing** rather than an empty table.
 * A blank panel is indistinguishable from one that failed, and a tab that is
 * simply absent tells the reader nothing at all — which is also why the strip
 * does not change shape the day an endpoint lands, and why five of them since
 * have.
 *
 * One remains: Summary, which needs an endpoint in quevee.
 *
 * The gaps are tracked in `docs/TODO.md`, not here: this file says what a view
 * *is*, and the backlog says when it gets a source.
 */

import { SeasonalityPanel } from "./SeasonalityPanel";
import { can as canHold } from "../lib/entitlements";
import { PRO_CAP as SEASONALITY_PRO } from "../lib/seasonality-screen";
import { createContext, Fragment, useContext, useEffect, useLayoutEffect, useRef, useState, type ReactElement } from "react";

import { useEntitlements } from "../lib/entitlements";
import { withoutPd } from "../lib/pd-metrics";
import { quarterColumns, quarterEstimate, withEstimate, yearEstimate } from "../lib/fin-quarters";
import { useFinStGrain } from "./fin-st-grain";
import { useMediaQuery } from "../lib/use-narrow";
import { AI_REVIEW_LABEL, FIN_ST_LABEL, dataTabAvailableFor, type DataTab, type PipView } from "../lib/pip";
import { BarIntelView } from "./BarIntelView";
import { HoldingsView } from "./HoldingsView";
import { EtfComponentsView, EtfHoldersView } from "./EtfViews";
import { CongressView } from "./CongressView";
import { symbolIndex, symbolResolver, useSymbolIndex } from "../lib/symbol-index";
import { useFundamentals } from "../lib/symbol-fundamentals";
import { useCompanyProfile } from "../lib/symbol-profile";
import { useSummary } from "../lib/symbol-summary";
import { useNews } from "../lib/symbol-news";
import type { AnnualRow, FundamentalMetric, NewsArticle, SymbolFundamentals } from "../api/symbols";
import type { PersonalPart, SummaryUsage } from "../api/analyst";
import { formatComposite, isComposite } from "../lib/composite";
import { QUEVEE_TOOLTIP, QueVeeMark } from "./QueVeeMark";
import { formatCap, formatPrice, marketForSymbol } from "../lib/market";

/**
 * The PIP's own type scale, measured from the box it was given.
 *
 * **A PIP has three sizes and they are a third apart.** `PIP_FRACTIONS` is 0.2,
 * 0.26 and 0.34 of the chart, so on a 1400px chart the panel is 280, 364 or 476
 * pixels wide — and type set for the narrowest is needlessly small in the widest
 * while type set for the widest does not fit the narrowest at all.
 *
 * So one base size is measured and **everything under it is expressed in `em`**.
 * Changing one number then rescales the whole panel, rather than each style
 * needing its own branch on a size the styles cannot see: they are module-level
 * constants, which is exactly why they cannot call a hook or read a prop.
 *
 * Width rather than height, because the constraint that actually bites is
 * horizontal — a table that does not fit scrolls sideways, where one line too
 * many merely gets cut.
 */
function usePipScale(): [React.RefObject<HTMLDivElement>, number] {
  // `useRef<T>(null)` rather than `useRef<T | null>`: this React's ref prop
  // takes the first and refuses the second.
  const host = useRef<HTMLDivElement>(null);
  const [base, setBase] = useState(PIP_BASE_MIN);

  useEffect(() => {
    const node = host.current;
    if (!node) return;
    const measure = () => {
      const width = node.clientWidth;
      if (width > 0) {
        setBase(Math.min(PIP_BASE_MAX, Math.max(PIP_BASE_MIN, width / PIP_BASE_DIVISOR)));
      }
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [host, base];
}

/**
 * The bounds of that scale, and the divisor that walks between them.
 *
 * 11px is the floor and 15 the ceiling. The floor was 9.5, which is where the
 * narrowest PIP sat permanently — `0.2` of a 1400px chart is 280px, and 280/34
 * lands under the old floor, so the small PIP was pinned to the smallest type
 * the scale could produce and never moved off it. That was too small to read
 * comfortably, which is the whole job of a summary panel.
 *
 * The divisor comes down with them so the middle of the range moves too, rather
 * than every size clamping to the new floor: 280/28 still clamps to 11, 364/28
 * is ~13, and the widest clamps to 15.
 *
 * The cost is horizontal: the binding constraint on this panel is width, so
 * larger type means a metric table in the *narrow* PIP scrolls sideways sooner.
 * That is the trade taken deliberately — a number that has to be scrolled to is
 * better than one that cannot be read.
 */
const PIP_BASE_MIN = 11;
const PIP_BASE_MAX = 15;
const PIP_BASE_DIVISOR = 28;

/** A headline row, in `em`, so the count that fits scales with the type. */
const PIP_NEWS_ROW_EM = 1.75;

/**
 * True inside the phone's Data cards (`DataCards`): a view drawn there must fit
 * 390px with nothing scrolling sideways, or it takes the swipe (owner,
 * 2026-10-14; mobile.md). Profile and Peers read it and narrow themselves; the
 * desktop and the PIP never set it.
 */
export const PhoneCard = createContext(false);

export interface SymbolViewProps {
  symbol: string;
  /** The PIP's form: fewer rows, shorter labels, smaller type. */
  compact?: boolean;
  /**
   * Ask the host to show a different symbol *in this panel*.
   *
   * Deliberately not a chart re-point: clicking a peer is an exploration, and
   * taking the chart with it would lose the thing the reader was looking at in
   * order to glance at a comparison. The host keeps a trail so the way back is
   * one click — which matters because the peer list of the peer is a different
   * list, and without a trail there is no route home.
   */
  onSymbol?: (symbol: string) => void;
  /**
   * Which chart group this panel was opened for.
   *
   * An id rather than a getter: the snapshot is asynchronous — capturing the
   * image waits for SciChart to blit WebGL into its 2D canvas — and only one of
   * the six views ever asks for it. `lib/chart-snapshot.ts` turns the id into
   * the data at the moment it is needed.
   *
   * Absent where there is no chart to describe, and a summary written without
   * one says so rather than pretending.
   */
  chartGroupId?: string;
}

export interface SymbolView {
  id: DataTab;
  /** What the chip and the tab both call it. */
  label: string;
  /**
   * Whether anything can answer this view today.
   *
   * Read by the hosts to mark a tab, and worth carrying rather than inferring:
   * a view that renders an empty state and a view whose fetch happened to return
   * nothing look identical from outside.
   */
  ready: boolean;
  Render: (props: SymbolViewProps) => ReactElement;
}

/**
 * What the Fundamentals view is called, in one place.
 *
 * **"Fin St", not "Fundamentals".** The word is twelve characters and the PIP
 * header lays its six views out side by side in a strip 170px wide — one entry
 * that long crowds out the five beside it. Short enough to sit in that row is
 * the constraint the name has to meet, and financial statements are what the
 * view actually shows.
 *
 * A constant rather than the literal twice, because the view prints its own name
 * in its empty state as well as on its chip and tab: two literals is two names
 * that drift, and a panel headed "Fundamentals" under a tab reading "Fin St"
 * reads as two different views.
 */
const FUNDAMENTALS_LABEL = FIN_ST_LABEL;
/** The AI's note on the symbol (`SummaryView`). "Summary" until 2026-10-20 (owner:
 * "instead of Summary tab in Data Panel. Please change it to AI Review"); the id
 * stays `summary`, so saved panels keep their tab. */
const SUMMARY_LABEL = AI_REVIEW_LABEL;

/** Every view except `chart`, which only a PIP can show. */
export const SYMBOL_VIEWS: readonly SymbolView[] = [
  { id: "profile", label: "Profile", ready: true, Render: ProfileView },
  // What an ETF holds — on an ETF only, and opened for one (`SymbolDataTabs`).
  { id: "etfComponents", label: "Components", ready: true, Render: EtfComponentsView },
  { id: "peers", label: "Peers", ready: true, Render: PeersView },
  { id: "fundamentals", label: FUNDAMENTALS_LABEL, ready: true, Render: FundamentalsView },
  { id: "summary", label: SUMMARY_LABEL, ready: true, Render: SummaryView },
  { id: "news", label: "News", ready: true, Render: NewsView },
  { id: "sentiment", label: "Sentiment", ready: true, Render: SentimentView },
  // Seasonality — one window in every past year, Pro only (`symbolViewsFor`);
  // not a PIP view yet (docs/plans/seasonality-layout.md, step 6).
  { id: "seasonality", label: "Seasonality", ready: true, Render: ({ symbol }) => <SeasonalityPanel symbol={symbol} /> },
  // Bar Intelligence™ — the chart's levels and pattern stats, never a PIP view.
  { id: "barIntel", label: "Bar Intel", ready: true, Render: BarIntelView },
  // 13F: what the Top 15 funds hold of this symbol — US-listed only, and never a PIP view.
  { id: "holdings", label: "13F Funds", ready: true, Render: HoldingsView },
  // Which US ETFs hold this stock — a tab of its own beside 13F, never on an ETF
  // (owner, 2026-10-30: "keep ETF holding at same level").
  { id: "etfHolders", label: "ETFs", ready: true, Render: EtfHoldersView },
  // Senate and House trades — US-listed only, never a PIP view (docs/plans/congress-trades.md).
  { id: "congress", label: "Congress", ready: true, Render: CongressView },
];

/**
 * The views a host may offer for one symbol.
 *
 * **Hidden rather than dotted, and only here does that rule bend.** The strip
 * deliberately does not change shape — a tab that appears the day an endpoint
 * lands moves every tab beside it — but that argument is about *time*, and this
 * is about the symbol on screen. A News tab on `RELIANCE.NS` is not a promise,
 * it is a dead end, and there is nothing coming that will fill it.
 *
 * The rule itself is `viewAvailableFor` in `lib/pip.ts`, shared with the PIP's
 * chips: this file is components and that one is pure, so the list has to live
 * there — and a second copy is how the two surfaces end up disagreeing.
 */
export function symbolViewsFor(symbol: string): readonly SymbolView[] {
  const etf = isEtfSymbol(symbol);
  // Seasonality is Pro — hidden, not locked, without it (owner, 2026-10-22).
  return SYMBOL_VIEWS.filter(
    (v) => dataTabAvailableFor(v.id, symbol, etf) && (v.id !== "seasonality" || canHold(SEASONALITY_PRO)),
  );
}

/**
 * Whether the symbol index calls `symbol` an ETF. False until the index has
 * loaded, so an ETF shows the stock's tabs for that moment; a host that lists
 * tabs subscribes with `useSymbolIndex` and re-renders when it lands.
 */
export function isEtfSymbol(symbol: string): boolean {
  const s = symbol.toUpperCase();
  return symbolIndex()?.find((r) => r.symbol === s)?.isEtf === true;
}

export function symbolView(id: PipView | DataTab): SymbolView | undefined {
  return SYMBOL_VIEWS.find((v) => v.id === id);
}

/**
 * What the company is, then what it earns.
 *
 * **The description leads, because it is the one thing a ticker cannot say.**
 * Everything under it — sector, chief executive, headcount, listing date — is a
 * fact a reader looks up rarely and wants in a fixed place when they do, which
 * is why it is a dense grid rather than a list of rows.
 *
 * Then revenue and earnings across the same six columns the Fundamentals tab
 * uses, and the three valuation readings that answer "compared to what". Those
 * repeat deliberately: a reader who opened Profile to find out what the company
 * does should not have to change tab to find out whether it is growing.
 *
 * The index is the fallback throughout. `lib/symbol-index.ts` is already in
 * memory for the search box, so name, sector and market cap render on the first
 * frame and the vendor record fills in behind them.
 */
function ProfileView({ symbol, compact }: SymbolViewProps): ReactElement {
  const phone = useContext(PhoneCard);
  const [pipHost, pipBase] = usePipScale();
  const index = useSymbolIndex(true);
  const row = index?.find((r) => r.symbol === symbol.toUpperCase());
  // A composite is not a company. Blanked before the fetches rather than after,
  // so a ratio chart does not ask the profile and fundamentals endpoints for a
  // symbol that cannot exist — and so the panel says why it is empty instead of
  // rendering a column of em-dashes that reads as missing data.
  const ratio = isComposite(symbol, symbolResolver(index));
  const profile = useCompanyProfile(ratio ? "" : symbol);
  const fundamentals = useFundamentals(ratio ? "" : symbol, !compact);
  const market = marketForSymbol(symbol);

  if (ratio) {
    return (
      <Empty title={formatComposite(symbol)} note="a ratio has no company profile" compact={compact} />
    );
  }

  if (!index && !profile) return <Empty title={symbol} note="loading…" compact={compact} />;

  const name = profile?.name ?? row?.name;
  const sector = profile?.sector ?? row?.sector;
  const industry = profile?.industry ?? row?.industry;

  if (compact) {
    // **The description leads even here.** An earlier cut left it out on the
    // grounds that a paragraph does not belong in a corner of a chart — which
    // is true at the 168px floor and wrong at the widths this actually renders
    // at: `PIP_FRACTIONS` is 0.2 to 0.34 of the chart, so 280 to 476 pixels,
    // where three clamped lines are perfectly readable and are the one thing a
    // ticker cannot tell you.
    //
    // Name and exchange are gone with it. The PIP sits *on* the chart, whose
    // own toolbar already says which symbol this is, so a row repeating it
    // spends a fifth of the panel confirming something on screen twice over.
    // Last price goes for the same reason — the candles are behind it.
    // Two rows of two rather than four of one. They are four short values, and
    // stacking them spent four lines saying what two say — lines the
    // description then did not have.
    const pairs: [string, string][][] = [
      [
        ["Mkt cap", formatCap(profile?.marketCap ?? row?.marketCap, market) || "—"],
        [
          "Earnings",
          formatMetric(
            fundamentals?.metrics.nextEarningsDate ?? null,
            fundamentals?.catalog.find((m) => m.id === "nextEarningsDate"),
          ),
        ],
      ],
      [
        ["Sector", sector ?? "—"],
        ["Industry", industry ?? "—"],
      ],
    ];
    return (
      <div ref={pipHost} style={{ ...PIP_PROFILE, fontSize: pipBase }}>
        {/* **The description takes whatever is left, rather than a fixed three
            lines.** The panel is a fixed height now, so the leftover was dead
            space at the bottom of every PIP — and this is the one field with
            more to say than there is room for, so it is the one that should
            have the room. `flex: 1` with `overflow: hidden` fills the box and
            cuts at the last line that fits, and the full text is in the
            tooltip. */}
        <p style={PIP_DESC} title={profile?.description ?? undefined}>
          {profile?.description ?? ""}
        </p>
        <div style={PIP_PAIRS}>
          {pairs.map((pair, index) => (
            <Fragment key={index}>
              {pair.map(([label, value]) => (
                <Fragment key={label}>
                  <span style={PIP_PAIR_K}>{label}</span>
                  <span style={PIP_PAIR_V} title={value}>
                    {value}
                  </span>
                </Fragment>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
    );
  }

  const facts: [string, string][] = [
    ["Sector", sector ?? "—"],
    ["Industry", industry ?? "—"],
    // The regulator's own classification, which is neither vendor's opinion.
    //
    // Title-cased, and the cell is bounded: the vendor shouts it — ELECTRONIC
    // COMPUTERS — and at `white-space: nowrap` that one value was wide enough to
    // put a horizontal scrollbar under the whole grid. The full text stays in
    // the tooltip, so nothing is lost by truncating it.
    ["SIC", sicLabel(profile?.sicCode, profile?.sicDescription)],
    ["Exchange", profile?.exchangeName ?? profile?.exchangeCode ?? row?.exchange ?? "—"],
    ["Listed", profile?.listedOn ?? "—"],
    ["CEO", profile?.ceo ?? "—"],
    ["Employees", profile?.employees ? profile.employees.toLocaleString() : "—"],
    ["Headquarters", [profile?.city, profile?.state, profile?.country].filter(Boolean).join(", ") || "—"],
    ["Market cap", formatCap(profile?.marketCap ?? row?.marketCap, market) || "—"],
    ["Shares", profile?.sharesOutstanding ? formatCompact(profile.sharesOutstanding) : "—"],
    ["Beta", profile?.beta === undefined ? "—" : profile.beta.toFixed(2)],
    ["52-week range", profile?.yearRange ?? "—"],
    ["Currency", profile?.currency ?? market.currency ?? "—"],
    ["ISIN", profile?.isin ?? "—"],
  ];

  return (
    <div style={SCROLL}>
      <div style={PROFILE_HEAD}>
        <span style={PROFILE_NAME}>{name ?? symbol}</span>
        {row?.isEtf || profile?.isEtf ? <span style={PROFILE_BADGE}>ETF</span> : null}
        {profile?.trading === false ? <span style={PROFILE_BADGE}>not trading</span> : null}
      </div>

      {profile?.description ? (
        <p style={PROFILE_DESC}>{profile.description}</p>
      ) : profile === undefined ? (
        <p style={PROFILE_DESC}>Loading the company description…</p>
      ) : (
        <p style={PROFILE_DESC}>No published description for this symbol.</p>
      )}

      {profile?.website ? (
        <a href={profile.website} target="_blank" rel="noreferrer" style={PROFILE_LINK}>
          {profile.website.replace(/^https?:\/\//, "")}
        </a>
      ) : null}

      {phone ? (
        <PhonePairs pairs={facts} />
      ) : (
      <div style={FACT_SCROLL}>
        <table style={FACT_TABLE}>
          <tbody>
            {Array.from({ length: Math.ceil(facts.length / 2) }, (_, pairIndex) => {
              const stripe =
                pairIndex % 2 === 1 ? { background: "var(--qf-row-alt)" } : undefined;
              const pair = [facts[pairIndex * 2], facts[pairIndex * 2 + 1]];
              return (
                <tr key={pairIndex} style={stripe}>
                  {pair.map((entry, column) => {
                    const seam = column === 0 ? undefined : FACT_SEAM;
                    return (
                      <Fragment key={column}>
                        <td style={{ ...FACT_K, ...seam }}>{entry?.[0] ?? ""}</td>
                        <td style={PROFILE_V} title={entry?.[1]}>
                          {entry?.[1] ?? ""}
                        </td>
                      </Fragment>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {fundamentals ? <ProfileEarnings data={fundamentals} /> : null}
    </div>
  );
}

/** `3571` + `ELECTRONIC COMPUTERS` -> `3571 · Electronic Computers`. */
function sicLabel(code: string | undefined, description: string | undefined): string {
  if (!code) return "—";
  if (!description) return code;
  const titled = description
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
  return `${code} · ${titled}`;
}

/**
 * Revenue, EPS and the three "compared to what" readings, inside Profile.
 *
 * The same components the Fundamentals tab uses, on the same data — one
 * definition, so a revenue figure cannot read one way here and another there.
 * Only the first group of the year table, because Profile is answering "is this
 * business growing" and not "is the balance sheet sound".
 */
function ProfileEarnings({ data }: { data: SymbolFundamentals }): ReactElement | null {
  const phone = useContext(PhoneCard);
  const by = new Map(data.catalog.map((m) => [m.id, m]));
  // A phone card has room for three years, as the PIP and the Fin St card do.
  const years = phone ? (data.annual ?? []).slice(-3) : (data.annual ?? []);
  const group = QUICK_ANNUAL[0];
  if (years.length === 0 || !group) return null;

  const rows = group.rows.filter((row) =>
    years.some((year) => year[row.id] !== null && year[row.id] !== undefined),
  );

  const valuation: FactSpec[] = [
    { id: "peg", label: "PEG" },
    { id: "peRangePos", label: "P/E in own 5y range", suffix: "%" },
    { id: "pePremium", label: "P/E vs own 5y avg" },
    { id: "nextEarningsDate", label: "Next earnings" },
    { id: "epsEstimate", label: "Est EPS" },
    { id: "revenueEstimate", label: "Est revenue" },
  ].filter((fact) => (data.metrics[fact.id] ?? data.depth[fact.id]) !== null
    && (data.metrics[fact.id] ?? data.depth[fact.id]) !== undefined);

  return (
    <>
      {rows.length > 0 ? (
        <div style={YEAR_SCROLL}>
          <table style={YEAR_TABLE}>
            <thead>
              <tr>
                <th style={YEAR_HEAD_K} />
                {years.map((year) => (
                  <th
                    key={year.label}
                    style={year.label === "TTM" ? YEAR_HEAD_NOW : YEAR_HEAD}
                    title={stampOfYear(year)}
                  >
                    {year.label}
                    {year.pit === false ? <span style={ESTIMATED}>*</span> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th style={YEAR_GROUP} colSpan={years.length + 1}>
                  {group.title}
                </th>
              </tr>
              {rows.map((row, index) => (
                <YearRow
                  key={row.id}
                  row={row}
                  metric={by.get(row.format ?? row.id)}
                  years={years}
                  alt={index % 2 === 1}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {valuation.length > 0 && phone ? (
        <PhonePairs
          pairs={valuation.map((fact) => {
            const raw = data.metrics[fact.id] ?? data.depth[fact.id] ?? null;
            const metric = by.get(fact.id);
            return [fact.label ?? metric?.label ?? fact.id, formatMetric(raw, metric) + (raw === null ? "" : (fact.suffix ?? ""))];
          })}
        />
      ) : valuation.length > 0 ? (
        <div style={FACT_SCROLL}>
          <table style={FACT_TABLE}>
            <tbody>
              {/* Two pairs to a row rather than four stacked rows: they are four
                  short readings, and a single column of them leaves two thirds
                  of a 560px panel empty while pushing everything below it down. */}
              {Array.from({ length: Math.ceil(valuation.length / 2) }, (_, rowIndex) => {
                const stripe =
                  rowIndex % 2 === 1 ? { background: "var(--qf-row-alt)" } : undefined;
                const pair = [valuation[rowIndex * 2], valuation[rowIndex * 2 + 1]];
                return (
                  <tr key={rowIndex} style={stripe}>
                    {pair.map((fact, column) => {
                      const seam = column === 0 ? undefined : FACT_SEAM;
                      if (!fact) {
                        return (
                          <Fragment key={column}>
                            <td style={{ ...FACT_K, ...seam }} />
                            <td style={FACT_V} />
                          </Fragment>
                        );
                      }
                      const raw = data.metrics[fact.id] ?? data.depth[fact.id] ?? null;
                      const metric = by.get(fact.id);
                      const hint = metric?.caveat || metric?.description;
                      return (
                        <Fragment key={fact.id}>
                          <td style={{ ...FACT_K, ...seam }} title={hint}>
                            {fact.label ?? metric?.label ?? fact.id}
                          </td>
                          <td style={FACT_V} title={hint}>
                            {formatMetric(raw, metric)}
                            {raw === null ? "" : (fact.suffix ?? "")}
                          </td>
                        </Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}
    </>
  );
}

/**
 * Comparable companies, priced now — and each one a way in.
 *
 * **The order is the vendor's and is preserved.** It is a relevance ranking,
 * and re-sorting it alphabetically would throw away the only piece of judgement
 * the endpoint supplies. Prices are decorated server-side from `live_quotes`,
 * because the vendor's own price is cached to the day and ours is today's.
 *
 * **Clicking one moves the panel, not the chart.** A peer is a comparison, and
 * taking the chart along would lose the thing being compared *to*. The host
 * keeps a trail so the way back is one click — which is not a nicety: the peer
 * list of a peer is a different list, so without a trail there is no route home
 * except remembering the ticker you came from.
 */
function PeersView({ symbol, compact, onSymbol }: SymbolViewProps): ReactElement {
  const phone = useContext(PhoneCard);
  const [pipHost, pipBase] = usePipScale();
  const data = useFundamentals(symbol, true);
  const market = marketForSymbol(symbol);
  const index = useSymbolIndex(true);

  if (!data) return <Empty title="Peers" note="loading…" compact={compact} />;
  if (data.peers.length === 0) {
    return <Empty title="Peers" note="no peers published for this symbol" compact={compact} />;
  }

  const rows = compact ? data.peers.slice(0, 8) : data.peers;

  if (phone) {
    // Two lines a peer instead of four columns: symbol and change, then the
    // name and the price — the table's 460px does not fit a phone card.
    return (
      <div style={{ padding: 12, display: "flex", flexDirection: "column" }}>
        {rows.map((peer) => {
          const change = peer.changePercentCc;
          const tone = change == null ? undefined : change > 0 ? "good" : change < 0 ? "bad" : undefined;
          const name = index?.find((r) => r.symbol === peer.symbol)?.name ?? "";
          return (
            <div key={peer.symbol} style={PEER_ROW}>
              <div style={PEER_LINE}>
                {onSymbol ? (
                  <button type="button" style={{ ...PEER_LINK, fontWeight: 700, fontSize: 15 }} onClick={() => onSymbol(peer.symbol)}>
                    {peer.symbol}
                  </button>
                ) : (
                  <span style={{ fontWeight: 700, fontSize: 15 }}>{peer.symbol}</span>
                )}
                <span style={{ fontVariantNumeric: "tabular-nums", color: tone ? TONE_COLOUR[tone] : undefined }}>
                  {change == null ? "—" : formatPercent(change / 100)}
                </span>
              </div>
              <div style={{ ...PEER_LINE, fontSize: 12, color: "var(--qf-text-muted)" }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>{name || "—"}</span>
                <span style={{ fontVariantNumeric: "tabular-nums", flex: "0 0 auto" }}>
                  {formatPrice(peer.close ?? undefined, market) || "—"}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // **The same table at both sizes**, rather than a list in the PIP and a table
  // in the panel. Two shapes for one thing is two things to learn, and the PIP
  // is 280 to 476 pixels — wide enough for four columns once the name is
  // allowed to truncate. Only the density and the row count change.
  const body = (
    <div
      ref={compact ? pipHost : undefined}
      style={compact ? { ...SCROLL_COMPACT, fontSize: pipBase } : FACT_SCROLL}
    >
      <table style={compact ? PEER_TABLE_COMPACT : FACT_TABLE}>
        <thead>
          <tr>
            <th style={FACT_HEAD_FIRST}>Symbol</th>
            <th style={PEER_HEAD_NAME}>Name</th>
            <th style={FACT_HEAD_V}>Last</th>
            <th style={FACT_HEAD_V}>Change</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((peer, rowIndex) => {
            const stripe =
              rowIndex % 2 === 1 ? { background: "var(--qf-row-alt)" } : undefined;
            const change = peer.changePercentCc;
            const tone =
              change == null ? undefined : change > 0 ? "good" : change < 0 ? "bad" : undefined;
            const name = index?.find((r) => r.symbol === peer.symbol)?.name ?? "";
            return (
              <tr key={peer.symbol} style={stripe}>
                <td style={FACT_K}>
                  {onSymbol ? (
                    <button
                      type="button"
                      style={PEER_LINK}
                      onClick={() => onSymbol(peer.symbol)}
                      title={`Show ${peer.symbol} in this panel`}
                    >
                      {peer.symbol}
                    </button>
                  ) : (
                    peer.symbol
                  )}
                </td>
                <td style={PEER_NAME} title={name}>
                  {name || "—"}
                </td>
                <td style={FACT_V}>{formatPrice(peer.close ?? undefined, market) || "—"}</td>
                <td style={{ ...FACT_V, color: tone ? TONE_COLOUR[tone] : FACT_V.color }}>
                  {change == null ? "—" : formatPercent(change / 100)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return body;
}

/**
 * The financials, at both sizes.
 *
 * **Compact is six rows, not a scaled panel.** The PIP gets the six that answer
 * "is this worth a closer look" — the grade first, because it is the one figure
 * ranking this symbol against every other — and asks the server to skip the two
 * per-symbol vendor fetches, since it has no room for a Piotroski score anyway.
 *
 * **The full form is grouped by category, in the server's order.** Nothing here
 * decides what a category holds or what a metric is called: `metrics.py` carries
 * the label, unit, precision and caveat, which is what stops this panel and the
 * screener grid disagreeing about what "P/E" means.
 *
 * A guessed publication date is marked. `pit: false` means the figures rest on a
 * date nobody filed, and a panel that hid that would defeat the whole
 * point-in-time design.
 */
function FundamentalsView({ symbol, compact, chartGroupId }: SymbolViewProps): ReactElement {
  const data = useFundamentals(symbol, !compact);

  if (!data) return <Empty title={FUNDAMENTALS_LABEL} note="loading…" compact={compact} />;
  if (Object.keys(data.metrics).length === 0) {
    return (
      <Empty
        title={FUNDAMENTALS_LABEL}
        note="no statements for this symbol — funds, trusts and most ETFs file none"
        compact={compact}
      />
    );
  }

  const by = new Map(data.catalog.map((m) => [m.id, m]));

  if (compact) return <CompactFundamentals data={data} by={by} />;

  return <FullFundamentals data={data} chartGroupId={chartGroupId} />;
}

/**
 * The PIP's form: a short year table, then the readings that have no history.
 *
 * **The same split as the panel, at a third of the width.** Revenue, EPS and the
 * two return ratios are only worth anything as a trend, so they keep their
 * columns; pace, composite and the rest are single readings and stay rows. A
 * PIP that flattened the first four into current values would be six numbers
 * with nothing to compare them to, which is what the previous version was.
 *
 * **Three columns, not six**, and that is the one concession to the size. The
 * PIP is 280 to 476 pixels — a label column plus six years needs about 500 —
 * so it shows the two prior years and the current one, which is enough to see
 * a direction. The full six are one click away in the panel, and a horizontal
 * scrollbar inside a box 170 pixels tall is the alternative nobody wants.
 */
function CompactFundamentals({
  data,
  by,
}: {
  data: SymbolFundamentals;
  by: Map<string, FundamentalMetric>;
}): ReactElement {
  const [pipHost, pipBase] = usePipScale();
  const { can } = useEntitlements();
  const years = (data.annual ?? []).slice(-3);

  const trend: AnnualRowSpec[] = [
    { id: "revenue", label: "Revenue" },
    { id: "revenueGrowth", label: "% chg", indent: true, format: "revYoYReported", hint: "revenueGrowth" },
    { id: "eps", label: "EPS" },
    { id: "epsGrowth", label: "% chg", indent: true, format: "epsYoYReported", hint: "epsGrowth" },
    { id: "roce", label: "Ret on capital" },
    { id: "roe", label: "Ret on equity" },
  ].filter((row) =>
    years.some((year) => year[row.id] !== null && year[row.id] !== undefined),
  );

  const facts: FactSpec[] = withoutPd(
    [
      { id: "pace" },
      { id: "composite" },
      { id: "marketCap", label: "Market cap" },
      { id: "epsCagr3y", label: "EPS CAGR 3y" },
      { id: "peRangePos", label: "P/E in 5y range", suffix: "%" },
      { id: "nextEarningsDate", label: "Next earnings" },
    ],
    can("ind:PD"),
  );

  return (
    <div ref={pipHost} style={{ ...SCROLL_COMPACT, fontSize: pipBase }}>
      {years.length > 0 && trend.length > 0 ? (
        <table style={PIP_YEAR_TABLE}>
          <thead>
            <tr>
              <th style={PIP_YEAR_HEAD_K} />
              {years.map((year) => (
                <th
                  key={year.label}
                  style={year.label === "TTM" ? PIP_YEAR_HEAD_NOW : PIP_YEAR_HEAD}
                  title={stampOfYear(year)}
                >
                  {year.label}
                  {year.pit === false ? <span style={ESTIMATED}>*</span> : null}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trend.map((row, index) => (
              <YearRow
                key={row.id}
                row={row}
                metric={by.get(row.format ?? row.id)}
                years={years}
                alt={index % 2 === 1}
                compact
              />
            ))}
          </tbody>
        </table>
      ) : null}

      {/* Two pairs to a row: six values stacked one to a line is six lines of
          a box that has about ten, and they are short enough that two fit
          across even at the narrowest PIP. */}
      <div style={PIP_PAIRS}>
        {facts.map((fact) => {
          const raw = data.metrics[fact.id] ?? data.depth[fact.id] ?? null;
          const metric = by.get(fact.id);
          const tone = typeof raw === "string" ? VERDICT_TONE[raw] : undefined;
          const text =
            formatMetric(raw, metric) + (raw === null ? "" : (fact.suffix ?? ""));
          return (
            <Fragment key={fact.id}>
              <span style={PIP_PAIR_K} title={metric?.caveat || metric?.description}>
                {fact.label ?? metric?.label ?? fact.id}
              </span>
              <span
                style={{ ...PIP_PAIR_V, ...(tone ? { color: TONE_COLOUR[tone] } : {}) }}
                title={text}
              >
                {text}
              </span>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

/**
 * The QuickView band — years across, facts underneath.
 *
 * **Anything with a history is a row of years; anything without is a
 * fact/value pair below.** That split is the whole layout, and it is the one
 * a printed fundamental block has used for forty years: the reference product's panel leads
 * with revenue and EPS across eight quarters, MarketSurge's with EPS and sales
 * across eight years, and both then drop into a dense two-column grid for the
 * ratings and the single readings. A number with a trend and a number without
 * are different kinds of fact, and mixing them costs the reader the trend —
 * `debtToEquity 0.78` is a fact, and `2.37 → 0.78 over four years` is a
 * finding.
 *
 * **Revenue and EPS lead, always.** Every other row is read against them, and
 * they are the two a reader checks before deciding whether the rest is worth
 * reading.
 *
 * What is *here* rather than in the lens list below is the "highest signal"
 * set: return on capital, free cash flow yield, EV/EBITDA and PEG, with
 * revenue and EPS growth and the margin trend that says whether the growth is
 * sustainable. The rest of the seventy-odd metrics are one click away and
 * unchanged.
 */
interface AnnualRowSpec {
  id: string;
  /** Overrides the metric table's grid header, which is cut for ~90px. */
  label?: string;
  /** A percentage change under the figure it belongs to, as `% chg` reads. */
  indent?: boolean;
  /**
   * The catalog metric to format and explain this row by, when `id` is not one.
   * The Quarters view's reported-EPS rows are computed on the client, so they
   * borrow `eps` and the reported growth metrics and read like the rows beside them.
   *
   * **The growth rows need one.** `revenueGrowth` / `epsGrowth` are annual-series
   * columns, not catalog metrics, so formatting by their own id finds no unit and
   * prints the fraction bare — `2.88` for +288%. They borrow a reported growth
   * metric for its `percent` unit; a GAAP row keeps `hint` on its own id so its
   * hover does not describe reported EPS.
   */
  format?: string;
  /** The catalog metric whose explanation this row's hover shows, if not `format`'s. */
  hint?: string;
}

const QUICK_ANNUAL: readonly { title: string; rows: readonly AnnualRowSpec[] }[] = [
  {
    title: "Revenue and earnings",
    rows: [
      { id: "revenue", label: "Revenue" },
      { id: "revenueGrowth", label: "% chg", indent: true, format: "revYoYReported", hint: "revenueGrowth" },
      { id: "eps", label: "EPS" },
      { id: "epsGrowth", label: "% chg", indent: true, format: "epsYoYReported", hint: "epsGrowth" },
    ],
  },
  {
    title: "Quality — what it earns on what it uses",
    rows: [
      { id: "roce", label: "Return on capital" },
      { id: "roe", label: "Return on equity" },
      { id: "fcfYield", label: "FCF yield" },
      { id: "grossMargin", label: "Gross margin" },
      { id: "operatingMargin", label: "Operating margin" },
    ],
  },
  {
    title: "What the market pays",
    rows: [
      { id: "pe", label: "P/E" },
      { id: "evEbitda", label: "EV / EBITDA" },
      { id: "earningsYield", label: "Earnings yield" },
      { id: "pb", label: "P/B" },
    ],
  },
  {
    title: "Standing",
    rows: [
      { id: "debtToEquity", label: "Debt / equity" },
      { id: "interestCoverage", label: "Interest coverage" },
      { id: "currentRatio", label: "Current ratio" },
    ],
  },
  {
    // The cash amount first and the yield under it, because they answer
    // different questions: $1.06 a share is what arrives, and 0.34% is what it
    // is worth at *this* price. A yield alone cannot say whether the dividend
    // grew or the price fell.
    title: "Returned to holders",
    rows: [
      { id: "dividendPerShare", label: "Dividend / share" },
      { id: "divYield", label: "Dividend yield", indent: true },
      { id: "payoutRatio", label: "Payout ratio" },
    ],
  },
];

/**
 * The **Quarters** view's rows (owner, 2026-10-07): only what one quarter can
 * say on its own. P/E, ROE, debt/equity and the yields need a year or a price,
 * so they stay in Years rather than appearing here as a TTM in disguise.
 *
 * EPS twice — the reported figure a company headlines, which the screener's
 * EPS YoY/QoQ use, and GAAP, which the Years view shows. They differ by a lot
 * (NVDA's July 2023 quarter: +440% against +862%), and both were asked for.
 */
const QUICK_QUARTER: readonly { title: string; rows: readonly AnnualRowSpec[] }[] = [
  {
    title: "Revenue and earnings",
    rows: [
      { id: "revenue", label: "Revenue" },
      { id: "revenueGrowth", label: "% chg YoY", indent: true, format: "revYoYReported" },
      { id: "revenueQoQ", label: "% chg QoQ", indent: true, format: "revQoQReported" },
      { id: "epsReported", label: "EPS (reported)", format: "eps", hint: "epsYoYReported" },
      { id: "epsReportedYoY", label: "% chg YoY", indent: true, format: "epsYoYReported" },
      { id: "epsReportedQoQ", label: "% chg QoQ", indent: true, format: "epsQoQReported" },
      { id: "eps", label: "EPS (GAAP)" },
      { id: "epsGrowth", label: "% chg YoY", indent: true, format: "epsYoYReported", hint: "epsGrowth" },
    ],
  },
  {
    title: "Margins",
    rows: [
      { id: "grossMargin", label: "Gross margin" },
      { id: "operatingMargin", label: "Operating margin" },
      { id: "netMargin", label: "Net margin" },
    ],
  },
  {
    title: "Cash",
    rows: [{ id: "freeCashFlow", label: "Free cash flow" }],
  },
  {
    title: "Returned to holders",
    rows: [{ id: "dividendPerShare", label: "Dividend / share" }],
  },
];

/**
 * The facts with no year column, in the order they decide something.
 *
 * They sit **below** the years rather than beside them, for the reason the
 * earlier draft of this panel got wrong: salting a single reading through a
 * table of years gives it five blank cells, and five blank cells read as
 * missing data rather than as a metric that never had a history.
 */
/**
 * The facts with no year column, in three named columns of six.
 *
 * **Three questions, side by side, rather than one long list.** Is the business
 * sound, is the price sensible, and is it growing — which is the order the
 * questions get asked in and the reason a column heading beats a category name.
 * A reader scanning "Quality / Safety" already knows what a bad answer looks
 * like; scanning "Worksheet" they do not.
 *
 * Fixed positions, so the three columns stay in step. A metric that is null for
 * this symbol keeps its row and renders a dash rather than pulling everything
 * below it up by one — three columns that shift independently stop being rows
 * at all.
 */
interface FactSpec {
  id: string;
  label?: string;
  /** Appended after the value, for a metric whose unit the table cannot say. */
  suffix?: string;
  /**
   * Show `low – high` from these two metrics instead of `id`'s own value. `id`
   * still names the row for its hover, since the range is what it describes.
   */
  range?: readonly [string, string];
}

const QUICK_FACT_COLUMNS: readonly { title: string; rows: readonly FactSpec[] }[] = [
  {
    title: "Quality / Safety",
    rows: [
      { id: "rating" },
      { id: "stance" },
      { id: "pace" },
      { id: "composite" },
      // The four earnings and revenue streaks as one score. Here rather than in
      // Growth because it measures *how reliably* a business grows, not how
      // fast — a steady 8% compounder scores above a lumpy 40% one.
      // No `label` override: the catalog says "Consistency" now, in one place,
      // and a second copy here is the drift the override was working around.
      { id: "consistency" },
      { id: "piotroski", label: "Piotroski F" },
      { id: "altmanZ", label: "Altman Z" },
    ],
  },
  {
    title: "Valuation",
    rows: [
      { id: "peg", label: "PEG" },
      // Directly under PEG, because the pair is the reading: the gap between
      // them is what the dividend is worth, and apart they are two ratios the
      // reader has to hold in their head.
      { id: "pegy", label: "PEGY (with dividend)" },
      // The two ways to ask "expensive compared to what", and they are not the
      // same question. The range position says where in its own five-year band
      // the multiple sits; the premium says how far it is from the middle of
      // that band. A stock can sit at the top of a narrow range and carry
      // almost no premium.
      // The range itself, then where today sits in it: 68% of 18–42 and 68%
      // of 30–34 are different stocks, and the percentage alone cannot say which.
      { id: "peRangePos", label: "P/E in own 5y range", range: ["pe5yMin", "pe5yMax"] },
      { id: "peRangePos", label: "P/E position in range", suffix: "%" },
      { id: "pePremium", label: "P/E vs own 5y avg" },
      { id: "buyBelow", label: "Buy below" },
      { id: "pctOffTarget", label: "Price vs buy below" },
      // **The intrinsic-value answers, beside the multiples that ask the same
      // question a different way.** A P/E says expensive *compared to its own
      // history*; these say expensive compared to what the business is worth.
      // They lived only on the server's `value` and `expectations` lenses, and
      // the lens chips were removed when the QuickView band landed — so nothing
      // rendered them at all and they read as missing rather than as elsewhere.
      //
      // The sticker is the estimate and the safety price is what to act on, so
      // the distance sits with them.
      //
      // **No `dcfValue` or `dcfMargin` here.** `derive.py` lists them under
      // "What is deliberately null" — they need a per-symbol beta for CAPM and
      // a CPI series to deflate ten years of earnings, and that pass has
      // neither. Rows for them would render an em dash forever, which reads as
      // a broken panel rather than as a number nobody computes yet.
      { id: "stickerPrice", label: "Sticker price" },
      { id: "marginOfSafetyPrice", label: "Margin of safety price" },
      // **"below sticker", because the denominator is the sticker price.**
      // Labelled "Price vs safety price" first, which was wrong and unusually
      // misleading: the reading is `(sticker - price) / sticker`, so it is
      // **+50% at the safety price** and 0% at the sticker. A label naming the
      // safety price implies zero there, which is off by the whole margin.
      //
      // No `suffix`: the unit is `percent`, so `formatMetric` appends the sign
      // already — `peRangePos` above needs one because its unit is `score`.
      { id: "stickerMargin", label: "Below sticker (buy at +50%)" },
      { id: "shareholderYield", label: "Shareholder yield" },
    ],
  },
  {
    title: "Growth",
    rows: [
      { id: "revenueCagr3y", label: "Revenue CAGR 3y" },
      { id: "epsCagr3y", label: "EPS CAGR 3y" },
      { id: "dividendStreak", label: "Years of rises" },
      { id: "marketCap", label: "Market cap" },
      { id: "nextEarningsDate", label: "Next earnings" },
      { id: "daysToEarnings", label: "Days until earnings" },
      // The consensus the print has to clear. A date says when to look and
      // nothing about what would count as good, so the two sit with it rather
      // than in Valuation — where they would read as a multiple's input.
      { id: "epsEstimate", label: "Est EPS" },
      { id: "revenueEstimate", label: "Est revenue" },
    ],
  },
];

/**
 * Which categorical verdicts read as good.
 *
 * Only these are coloured. A ratio has no threshold the client may invent —
 * whether 0.78 is a good debt/equity depends on the industry, and the server
 * ships `direction` precisely so nobody guesses. These are words whose meaning
 * *is* the judgement, so colouring them adds nothing that is not already said.
 */
const VERDICT_TONE: Record<string, "good" | "mid" | "bad"> = {
  "A+": "good", A: "good", "B+": "good", B: "mid", "C+": "mid", C: "bad", D: "bad", F: "bad",
  Buy: "good", Watch: "mid", "Value trap": "bad", Avoid: "bad",
  Sprint: "good", Quick: "good", Steady: "good", Slow: "mid", Stalled: "mid",
  Reversing: "bad", Recovering: "mid",
};

const TONE_COLOUR: Record<"good" | "mid" | "bad", string> = {
  good: "var(--qf-up, #1a7f37)",
  mid: "var(--qf-text)",
  bad: "var(--qf-down, #cf222e)",
};

function FundamentalsQuickView({
  data,
  chartGroupId,
}: {
  data: SymbolFundamentals;
  /** The chart whose saved Years/Quarters choice this table follows. */
  chartGroupId?: string;
}): ReactElement | null {
  const by = new Map(data.catalog.map((m) => [m.id, m]));

  /**
   * What the mouse is on, explained in full underneath.
   *
   * **The tooltip stays; this is the other half.** A `title` is the right tool
   * for a glance and the wrong one for a paragraph — it appears after a delay
   * the reader did not ask for, sits over the row it is describing, and cannot
   * be read at the same time as the number. Every caveat here is two or three
   * sentences carrying a rule of thumb, which is exactly the length a tooltip
   * handles worst.
   *
   * **The last hover persists rather than clearing on exit.** A box that empties
   * when the pointer moves is a box you cannot read: reaching it to read it is
   * what dismisses it. It clears when the symbol does, which is the only time
   * the explanation is about something no longer on screen.
   */
  const [hint, setHint] = useState<string | null>(null);
  const { can } = useEntitlements();
  const pd = can("ind:PD");
  const explain = (id: string) => () => setHint(id);
  /**
   * Years or Quarters, switched from the table's own top-left cell so the
   * choice costs no row (owner, 2026-10-07), and **saved per chart**
   * (`ChartGroup.finStGrain`, owner 2026-10-08) — see `fin-st-grain.tsx`.
   */
  const [grain, setGrain] = useFinStGrain(chartGroupId);
  const { annual, quarterly } = finStColumns(data);
  const years = grain === "quarters" ? quarterly : annual;
  const spec = grain === "quarters" ? QUICK_QUARTER : QUICK_ANNUAL;
  const value = (id: string) => data.metrics[id] ?? data.depth[id] ?? null;
  const hasEst = years.some((year) => year.estimate === true);
  const estDate = years.find((year) => year.estimate === true)?.estimateDate;
  // TTM is pinned after Trend, so only the periods are counted for fitting.
  const hasTtm = years.at(-1)?.label === "TTM";
  const [tintAt, setTintAt] = useGrowthTint();
  const [showTtm, setShowTtm] = useShowTtm();
  const [columnCount, setColumnCount] = useColumns();
  const touch = useMediaQuery(COARSE_POINTER);
  const periodCount = years.length - (hasTtm ? 1 : 0);
  const [fitRef, fitted] = useFitColumns(
    columnCount === null ? periodCount : 0,
    `${grain}:${data.symbol}:${showTtm}:${touch}:${columnCount}`,
  );
  // A fixed count is the newest that many, and scrolls if the box is too narrow
  // for it — the reader asked for them; Auto drops what does not fit.
  const from = columnCount === null ? fitted : Math.max(0, periodCount - columnCount);
  const order = periodOrder(years, from, showTtm);

  const groups = spec.map((group) => ({
    title: group.title,
    rows: withoutPd(group.rows, pd).filter((row) =>
      years.some((year) => year[row.id] !== null && year[row.id] !== undefined),
    ),
  })).filter((group) => group.rows.length > 0);

  // A column survives if anything in it answered; its individual rows keep
  // their place either way, so the three stay in step.
  //
  // **PD is withheld before that test, not after.** A column whose only
  // answering rows were PD's disappears rather than rendering as a heading over
  // nothing.
  const columns = QUICK_FACT_COLUMNS.map((column) => ({
    title: column.title,
    rows: withoutPd(column.rows, pd),
  })).filter((column) => column.rows.some((row) => value(row.id) !== null));
  const depth = Math.max(0, ...columns.map((column) => column.rows.length));
  if (groups.length === 0 && columns.length === 0) return null;

  const hinted = hint ? by.get(hint) : undefined;

  return (
    <div data-testid="fundamentals-quickview" onMouseLeave={() => undefined}>
      {years.length > 0 && groups.length > 0 ? (
        <div ref={fitRef} style={YEAR_SCROLL}>
          <table style={YEAR_TABLE}>
            <thead>
              <tr>
                <th style={YEAR_HEAD_K}>
                  <span style={HEAD_CONTROLS}>
                    {quarterly.length > 0 ? (
                      <GrainSwitch grain={grain} onGrain={setGrain} short touch={touch} />
                    ) : null}
                    <FinStSettings
                      tintAt={tintAt}
                      onTintAt={setTintAt}
                      showTtm={showTtm}
                      onShowTtm={setShowTtm}
                      hasTtm={hasTtm}
                      columns={columnCount}
                      onColumns={setColumnCount}
                      touch={touch}
                    />
                  </span>
                </th>
                <th
                  style={YEAR_HEAD_TREND}
                  title={from > 0 ? `Includes ${years.slice(0, from).map((y) => y.label).join(", ")}, too narrow to show` : undefined}
                >
                  Trend
                </th>
                {order.map((index) => years[index]!).map((year) => (
                  <th
                    key={year.label}
                    style={year.estimate === true ? YEAR_HEAD_EST : year.label === "TTM" ? YEAR_HEAD_TTM_FIRST : YEAR_HEAD}
                    title={year.estimate === true ? String(year.estimateNote ?? "") : stampOfYear(year)}
                  >
                    {year.label}
                    {year.estimate === true ? <span style={EST_MARK}> est</span> : null}
                    {year.pit === false ? <span style={ESTIMATED}>*</span> : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((group, groupIndex) => (
                <Fragment key={group.title}>
                  <tr>
                    {/* The estimate column's yellow sides run through the group
                        rows too, so it reads as one box from header to foot. */}
                    <th
                      style={groupIndex === 0 ? YEAR_GROUP : YEAR_GROUP_RULED}
                      colSpan={order.length + (hasEst ? 1 : 2)}
                    >
                      {group.title}
                    </th>
                    {hasEst ? (
                      <td style={groupIndex === 0 ? EST_DATE : { ...EST_SIDES, borderTop: YEAR_GROUP_RULED.borderTop }}>
                        {/* The report the column waits on, in the row's empty
                            cell (owner, 2026-10-28): `08-Jan`. */}
                        {groupIndex === 0 ? formatDayMonth(String(estDate ?? "")) : null}
                      </td>
                    ) : null}
                  </tr>
                  {group.rows.map((row, rowIndex) => (
                    <YearRow
                      key={row.id}
                      row={row}
                      metric={by.get(row.format ?? row.id)}
                      years={years}
                      alt={rowIndex % 2 === 1}
                      onHint={explain(row.hint ?? row.format ?? row.id)}
                      trend={grain}
                      from={from}
                      tintAt={tintAt}
                      showTtm={showTtm}
                      foot={groupIndex === groups.length - 1 && rowIndex === group.rows.length - 1}
                    />
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {columns.length > 0 ? (
        <div style={FACT_SCROLL}>
          <table style={FACT_TABLE}>
            <thead>
              <tr>
                {columns.map((column, columnIndex) => (
                  <Fragment key={column.title}>
                    <th style={columnIndex === 0 ? FACT_HEAD_FIRST : FACT_HEAD}>
                      {column.title}
                    </th>
                    <th style={FACT_HEAD_V}>Value</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: depth }, (_, index) => {
                const stripe =
                  index % 2 === 1 ? { background: "var(--qf-row-alt)" } : undefined;
                return (
                  <tr key={index} style={stripe}>
                    {columns.map((column, columnIndex) => {
                      const seam = columnIndex === 0 ? undefined : FACT_SEAM;
                      const fact = column.rows[index];
                      if (!fact) {
                        return (
                          <Fragment key={column.title}>
                            <td style={{ ...FACT_K, ...seam }} />
                            <td style={FACT_V} />
                          </Fragment>
                        );
                      }
                      const raw = value(fact.id);
                      const metric = by.get(fact.id);
                      const tone = typeof raw === "string" ? VERDICT_TONE[raw] : undefined;
                      const note = metric?.caveat || metric?.description;
                      return (
                        <Fragment key={column.title}>
                          <td
                            style={{ ...FACT_K, ...seam }}
                            title={note}
                            onMouseEnter={explain(fact.id)}
                          >
                            {fact.label ?? metric?.label ?? fact.id}
                          </td>
                          <td
                            style={{ ...FACT_V, color: tone ? TONE_COLOUR[tone] : FACT_V.color }}
                            title={note}
                            onMouseEnter={explain(fact.id)}
                          >
                            {fact.range
                              ? formatRange(fact.range, value, by)
                              : formatMetric(raw, metric) +
                                (raw === null || raw === undefined ? "" : (fact.suffix ?? ""))}
                          </td>
                        </Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      <div style={HINT_BOX}>
        {hinted ? (
          <>
            <span style={HINT_LABEL}>{hinted.label || hinted.id}</span>
            <span style={HINT_TEXT}>{hinted.description}</span>
            {hinted.caveat ? <span style={HINT_NOTE}>{hinted.caveat}</span> : null}
          </>
        ) : (
          <span style={HINT_IDLE}>
            Point at any row for what it measures and how to read it.
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * One metric across every year, each cell carrying its year-on-year mark.
 *
 * **The arrow says which way and the colour says whether that is welcome** —
 * two different questions. Debt/equity falling is a down arrow in green and
 * accruals rising is an up arrow in red, so colouring by direction alone would
 * call every increase good, which is wrong for about a third of these rows.
 * The metric table already carries the `direction` field that decides it, so
 * nothing here has a threshold of its own.
 */
function YearRow({
  row,
  metric,
  years,
  alt,
  onHint,
  compact,
  trend,
  from = 0,
  tintAt = GROWTH_TINT,
  showTtm = true,
  foot = false,
}: {
  row: AnnualRowSpec;
  metric: FundamentalMetric | undefined;
  years: SymbolFundamentals["annual"];
  /** Every second row of a group is tinted, to track six columns across. */
  alt: boolean;
  /**
   * Fired on hover, so a box below can explain this row.
   *
   * Optional: Profile borrows this table for revenue and EPS and has no
   * explanation box of its own, and a required callback would have made every
   * borrower invent a no-op.
   */
  onHint?: (() => void) | undefined;
  /** The PIP's density — smaller type and tighter cells, same structure. */
  compact?: boolean;
  /**
   * The full table's reading (owner, 2026-10-15): a micro chart of the whole row
   * after the label, and a tint on strong growth, in place of an arrow per cell.
   * The PIP and Profile keep their arrows — three columns is no trend to draw.
   *
   * `"quarters"` leaves the trailing TTM column out of the micro chart: twelve
   * months beside single quarters is four times the size for no reason but the
   * period, so it would be the tallest bar every time.
   * Beside fiscal years it is the same length of period, and stays in.
   */
  trend?: "years" | "quarters";
  /**
   * The first period drawn as a cell. Earlier ones are still in the micro chart
   * — only their cells are
   * dropped, oldest first, when the table is too narrow (`useFitColumns`).
   */
  from?: number;
  /** The % chg tint threshold, `null` for none (`FinStSettings`). */
  tintAt?: number | null;
  /** The TTM toggle: off drops its cell and its bar. */
  showTtm?: boolean;
  /** The table's last row, which closes the estimate column's yellow box. */
  foot?: boolean;
}): ReactElement {
  const values = years.map((year) => {
    const raw = year[row.id];
    return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
  });

  const stripe = alt ? { background: "var(--qf-row-alt)" } : undefined;

  const labelStyle = compact
    ? { ...(row.indent ? PIP_YEAR_K_INDENT : PIP_YEAR_K), ...stripe }
    : { ...(row.indent ? YEAR_K_INDENT : YEAR_K), ...stripe };

  return (
    <tr style={stripe} onMouseEnter={onHint}>
      <th style={labelStyle} title={metric?.caveat || metric?.description}>
        {row.label ?? metric?.label ?? row.id}
      </th>
      {trend ? (
        <td style={YEAR_TREND} data-trend>
          <TrendBars values={trendValues(years, values, trend, showTtm)} />
        </td>
      ) : null}
      {(trend ? periodOrder(years, from, showTtm) : values.map((_, i) => i)).map((index) => {
        const value = values[index] ?? null;
        const previous = index > 0 ? values[index - 1] ?? null : null;
        const now = years[index]?.label === "TTM";
        const est = years[index]?.estimate === true;
        // The current column is separated by a rule: everything left of it is
        // history and everything on it is now, and that is the one boundary in
        // the table a reader actually navigates by.
        const base = compact
          ? now
            ? PIP_YEAR_V_NOW
            : PIP_YEAR_V
          : est
            ? { ...YEAR_V_EST, ...(foot ? { borderBottom: EST_LINE } : {}) }
            : now
              ? YEAR_V_NOW
              : YEAR_V;
        if (value === null) {
          return (
            <td key={years[index]?.label ?? index} style={trend && now ? YEAR_V_TTM_FIRST : base}>
              —
            </td>
          );
        }
        if (trend) {
          // Only a % chg row is tinted, and by its own value (owner, 2026-10-15):
          // the figure rows' tint measured a move between columns, which the
          // micro chart already shows, and reads as noise beside it.
          const tint = row.indent ? growthTint(value, metric?.direction, tintAt) : null;
          // A negative figure reads red whatever the metric — a loss, a cash
          // burn, a shrinking quarter — the red its micro chart bar is drawn in.
          const style = {
            ...(now ? YEAR_V_TTM_FIRST : base),
            ...(tint ? { background: tint } : {}),
            ...(value < 0 ? { color: TONE_COLOUR.bad } : {}),
          };
          return (
            <td key={years[index]?.label ?? index} style={style}>
              {formatMetric(value, metric)}
            </td>
          );
        }
        let mark: ReactElement | null = null;
        // 0.05% of the previous value, so a rounding-level wobble does not earn
        // an arrow that reads as a change.
        if (previous !== null && Math.abs(value - previous) > Math.abs(previous || 1) * 0.0005) {
          const up = value > previous;
          const welcome = metric?.direction === "down" ? !up : up;
          const colour =
            metric?.direction === "neutral"
              ? "var(--qf-text-muted)"
              : TONE_COLOUR[welcome ? "good" : "bad"];
          mark = (
            <span style={{ ...MARK, color: colour }} aria-hidden="true">
              {up ? "▲" : "▼"}
            </span>
          );
        }
        return (
          <td key={years[index]?.label ?? index} style={base}>
            {formatMetric(value, metric)}
            {mark}
          </td>
        );
      })}
    </tr>
  );
}

/**
 * `[ Years | Quarters ]`, in the table's empty top-left cell. Two buttons rather
 * than a select: there are two choices and both should be visible at once.
 */
function GrainSwitch({
  grain,
  onGrain,
  short,
  touch,
}: {
  grain: "years" | "quarters";
  onGrain: (grain: "years" | "quarters") => void;
  /** `YR` / `QTR` (owner, 2026-10-15), so the tint chip fits beside it in the label column. */
  short?: boolean;
  /** Thumb-sized, on a touch screen. */
  touch?: boolean;
}): ReactElement {
  const option = (value: "years" | "quarters", text: string) => (
    <button
      type="button"
      aria-pressed={grain === value}
      aria-label={text}
      title={short ? text : undefined}
      onClick={() => onGrain(value)}
      style={{ ...(grain === value ? GRAIN_ON : GRAIN_OFF), ...(touch ? TOUCH_CONTROL : {}) }}
    >
      {short ? (value === "years" ? "YR" : "QTR") : text}
    </button>
  );
  return (
    <span role="group" aria-label="Periods" style={GRAIN_GROUP}>
      {option("years", "Years")}
      {option("quarters", "Quarters")}
    </span>
  );
}

/** Label and value, one pair to a line: the phone card's form of a two-column fact table. */
function PhonePairs({ pairs }: { pairs: readonly (readonly [string, string])[] }): ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, paddingTop: 8 }}>
      {pairs.map(([label, value], i) => (
        <div key={`${label}-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 13 }}>
          <span style={{ color: "var(--qf-text-muted)", flex: "0 0 auto" }}>{label}</span>
          <span style={{ textAlign: "right", minWidth: 0, overflowWrap: "anywhere" }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

const PEER_ROW: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 2, padding: "8px 0", borderBottom: "1px solid var(--qf-border)" };
const PEER_LINE: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, minWidth: 0 };

/**
 * Fin St on a phone: the Data tab's card form (owner, 2026-10-14;
 * `docs/features/mobile.md`, "On a phone the views are cards you swipe").
 *
 * The same rows, the same Years/Quarters choice saved per chart, the same PD
 * gating and formatting as the full view, shaped for 390px: **three periods at a
 * time** (the PIP chose three for the same width), with *‹ earlier* / *later ›*
 * to move the window, and every group **folded but the first**. The single
 * readings (quality, valuation, growth) follow as label/value lists.
 */
export function FinStCard({ symbol, chartGroupId }: SymbolViewProps): ReactElement {
  const data = useFundamentals(symbol, true);
  const { can } = useEntitlements();
  const pd = can("ind:PD");
  const [grain, setGrain] = useFinStGrain(chartGroupId);
  const [tintAt, setTintAt] = useGrowthTint();
  const [showTtm, setShowTtm] = useShowTtm();
  const [columns, setColumns] = useColumns();
  /** How many periods back from the newest the window ends: 0 shows the newest. */
  const [back, setBack] = useState(0);
  useEffect(() => setBack(0), [symbol, grain, showTtm, columns]);

  if (!data) return <Empty title={FUNDAMENTALS_LABEL} note="loading…" />;
  if (Object.keys(data.metrics).length === 0) {
    return <Empty title={FUNDAMENTALS_LABEL} note="no statements for this symbol — funds, trusts and most ETFs file none" />;
  }
  const by = new Map(data.catalog.map((m) => [m.id, m]));
  const { annual, quarterly } = finStColumns(data);
  const all = grain === "quarters" ? quarterly : annual;
  const spec = grain === "quarters" ? QUICK_QUARTER : QUICK_ANNUAL;
  // TTM is pinned first and does not page; the periods page behind it, two at
  // a time beside it and three without it (owner, 2026-10-15).
  const ttmIndex = all.at(-1)?.label === "TTM" ? all.length - 1 : -1;
  const ttm = ttmIndex >= 0 && showTtm ? all[ttmIndex] : undefined;
  const periods = all.filter((_, i) => i !== ttmIndex);
  const per = columns ?? (ttm ? 2 : 3);
  const end = Math.max(Math.min(periods.length, per), periods.length - back);
  const shown = [...(ttm ? [ttm] : []), ...periods.slice(Math.max(0, end - per), end)];
  const value = (id: string) => data.metrics[id] ?? data.depth[id] ?? null;

  const groups = spec
    .map((group) => ({
      title: group.title,
      rows: withoutPd(group.rows, pd).filter((row) =>
        all.some((year) => year[row.id] !== null && year[row.id] !== undefined),
      ),
    }))
    .filter((group) => group.rows.length > 0);
  const facts = QUICK_FACT_COLUMNS.map((column) => ({
    title: column.title,
    rows: withoutPd(column.rows, pd).filter((row) => value(row.id) !== null),
  })).filter((column) => column.rows.length > 0);

  const cols = `minmax(0, 1fr) ${FIN_TREND_W}px repeat(${shown.length}, 64px)`;
  const ttmCell = (year: (typeof all)[number]) =>
    year === ttm
      ? { borderRight: "1px solid var(--qf-border)", paddingRight: 4, fontWeight: 600 }
      : year.estimate === true
        ? { ...EST_SIDES, paddingRight: 3 }
        : {};
  return (
    <div style={FIN_CARD} data-testid="fin-st-card">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {quarterly.length > 0 ? <GrainSwitch grain={grain} onGrain={setGrain} short touch /> : null}
          <FinStSettings
            tintAt={tintAt}
            onTintAt={setTintAt}
            showTtm={showTtm}
            onShowTtm={setShowTtm}
            hasTtm={ttmIndex >= 0}
            columns={columns}
            onColumns={setColumns}
            touch
          />
        </span>
        <span style={{ display: "flex", gap: 6 }}>
          <button type="button" style={FIN_STEP} disabled={end - per <= 0} onClick={() => setBack((b) => b + 1)}>
            ‹ earlier
          </button>
          <button type="button" style={FIN_STEP} disabled={back === 0} onClick={() => setBack((b) => Math.max(0, b - 1))}>
            later ›
          </button>
        </span>
      </div>
      {shown.length > 0 && groups.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: cols, gap: "0 6px", fontSize: 12, color: "var(--qf-text-muted)" }}>
          <span />
          <span>Trend</span>
          {shown.map((year) => (
            <span
              key={year.label}
              style={{
                textAlign: "right",
                fontWeight: year === ttm ? 700 : 400,
                ...ttmCell(year),
                ...(year.estimate === true ? { borderTop: EST_LINE, color: "var(--qf-warn)" } : {}),
              }}
              title={year.estimate === true ? String(year.estimateNote ?? "") : undefined}
            >
              {year.label}
              {year.estimate === true ? " e" : ""}
              {year.pit === false ? "*" : ""}
            </span>
          ))}
        </div>
      )}
      {groups.map((group) => (
        <FinFold key={group.title} title={group.title} open>
          {group.rows.map((row) => {
            const metric = by.get(row.format ?? row.id);
            const values = all.map((year) => numberIn(year[row.id]));
            return (
              <div key={row.id} style={{ display: "grid", gridTemplateColumns: cols, gap: "0 6px", alignItems: "center" }}>
                <span style={{ paddingLeft: row.indent ? 12 : 0, color: row.indent ? "var(--qf-text-muted)" : "var(--qf-text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {row.label ?? metric?.label ?? row.id}
                </span>
                <span data-trend>
                  <TrendBars values={trendValues(all, values, grain, showTtm)} width={FIN_TREND_W} />
                </span>
                {shown.map((year) => {
                  const v = numberIn(year[row.id]);
                  // The full table's rules: only % chg is tinted, at the chip's
                  // threshold; a negative figure is red; no arrows.
                  const tint = v !== null && row.indent ? growthTint(v, metric?.direction, tintAt) : null;
                  return (
                    <span
                      key={year.label}
                      style={{
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                        borderRadius: 3,
                        ...(v !== null && v < 0 ? { color: TONE_COLOUR.bad } : {}),
                        ...(tint ? { background: tint } : {}),
                        ...ttmCell(year),
                      }}
                    >
                      {v === null ? "—" : formatMetric(v, metric)}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </FinFold>
      ))}
      {facts.map((column) => (
        <FinFold key={column.title} title={column.title} open>
          {column.rows.map((fact, i) => {
            const metric = by.get(fact.id);
            const raw = value(fact.id);
            const tone = typeof raw === "string" ? VERDICT_TONE[raw] : undefined;
            const text = fact.range
              ? `${formatMetric(value(fact.range[0]), by.get(fact.range[0]))} – ${formatMetric(value(fact.range[1]), by.get(fact.range[1]))}`
              : formatMetric(raw, metric) + (raw === null ? "" : (fact.suffix ?? ""));
            return (
              <div key={`${fact.id}-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                <span style={{ color: "var(--qf-text-muted)" }}>{fact.label ?? metric?.label ?? fact.id}</span>
                <span style={{ textAlign: "right", ...(tone ? { color: TONE_COLOUR[tone] } : {}) }}>{text}</span>
              </div>
            );
          })}
        </FinFold>
      ))}
    </div>
  );
}

/**
 * The growth a % chg cell is tinted at, by default (owner, 2026-10-15): +30%
 * green, −30% red. The reader changes it under the ⚙ beside YR / QTR.
 */
export const GROWTH_TINT = 0.3;

/** What the chip offers (owner, 2026-10-15). `null` is Off. */
export const GROWTH_TINT_CHOICES: readonly (number | null)[] = [null, 0.1, 0.3, 0.5, 1];

const GROWTH_TINT_KEY = "qf.finst.growthTint";

/**
 * The reader's threshold, per browser — `BarIntelView`'s view choice and its
 * reason: a display preference, not workspace data. Storage that throws, or a
 * value the chip no longer offers, reads as the default.
 */
function useGrowthTint(): [number | null, (at: number | null) => void] {
  const [at, setAt] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem(GROWTH_TINT_KEY);
      if (raw === "off") return null;
      const n = raw === null ? NaN : Number(raw);
      return GROWTH_TINT_CHOICES.includes(n) ? n : GROWTH_TINT;
    } catch {
      return GROWTH_TINT;
    }
  });
  const choose = (next: number | null) => {
    setAt(next);
    try {
      localStorage.setItem(GROWTH_TINT_KEY, next === null ? "off" : String(next));
    } catch {
      // the choice still holds for this page
    }
  };
  return [at, choose];
}

const SHOW_TTM_KEY = "qf.finst.ttm";

/**
 * Whether the TTM column shows (owner, 2026-10-15), per browser like the tint.
 * On by default; storage that throws reads as on.
 */
function useShowTtm(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_TTM_KEY) !== "off";
    } catch {
      return true;
    }
  });
  const choose = (next: boolean) => {
    setOn(next);
    try {
      localStorage.setItem(SHOW_TTM_KEY, next ? "on" : "off");
    } catch {
      // the choice still holds for this page
    }
  };
  return [on, choose];
}

/** A finger, not a mouse: the controls grow to a size a thumb can hit. */
const COARSE_POINTER = "(pointer: coarse)";

/** `±30%` — a tint choice, as the menu lists it. */
function tintLabel(at: number | null): string {
  return at === null ? "Off" : `±${Math.round(at * 100)}%`;
}

/** What Columns offers (owner, 2026-10-15): fit the box, or always this many periods. */
export const COLUMN_CHOICES: readonly number[] = [2, 3, 4, 5, 6, 7, 8];

const COLUMNS_KEY = "qf.finst.columns";

/**
 * The reader's column count, per browser: `null` is Auto (fit the box on the
 * table, two or three a page on the phone card), a number is always that many
 * periods, TTM aside. A count the menu no longer offers reads as Auto.
 */
function useColumns(): [number | null, (n: number | null) => void] {
  const [n, setN] = useState<number | null>(() => {
    try {
      const raw = Number(localStorage.getItem(COLUMNS_KEY));
      return COLUMN_CHOICES.includes(raw) ? raw : null;
    } catch {
      return null;
    }
  });
  const choose = (next: number | null) => {
    setN(next);
    try {
      localStorage.setItem(COLUMNS_KEY, next === null ? "auto" : String(next));
    } catch {
      // the choice still holds for this page
    }
  };
  return [n, choose];
}

/**
 * The table's settings behind one ⚙ (owner, 2026-10-15): the tint threshold,
 * TTM and the column count. Three controls in the header widened the label
 * column for every row beneath them; a menu costs one small button.
 *
 * Positioned `fixed` from the button, because the table scrolls in a box with
 * `overflow-x`, which would clip anything absolutely placed inside it.
 */
function FinStSettings({
  tintAt,
  onTintAt,
  showTtm,
  onShowTtm,
  hasTtm,
  columns,
  onColumns,
  touch,
}: {
  tintAt: number | null;
  onTintAt: (at: number | null) => void;
  showTtm: boolean;
  onShowTtm: (on: boolean) => void;
  hasTtm: boolean;
  columns: number | null;
  onColumns: (n: number | null) => void;
  touch?: boolean;
}): ReactElement {
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!at) return;
    const close = (event: Event) => {
      const target = event.target as Node | null;
      if (target && (menu.current?.contains(target) || button.current?.contains(target))) return;
      setAt(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAt(null);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [at]);
  const toggle = () => {
    if (at) return setAt(null);
    const box = button.current?.getBoundingClientRect();
    setAt({ top: (box?.bottom ?? 0) + 4, left: box?.left ?? 0 });
  };
  return (
    <>
      <button
        ref={button}
        type="button"
        aria-label="Table settings"
        aria-expanded={at !== null}
        title="Tint, TTM and columns"
        onClick={toggle}
        style={{ ...SETTINGS_BUTTON, ...(touch ? TOUCH_CONTROL : {}) }}
      >
        ⚙
      </button>
      {at ? (
        <div ref={menu} role="dialog" aria-label="Table settings" style={{ ...SETTINGS_MENU, top: at.top, left: at.left }}>
          <fieldset style={SETTINGS_GROUP}>
            <legend style={SETTINGS_LEGEND}>Tint % chg at</legend>
            {GROWTH_TINT_CHOICES.map((choice) => (
              <label key={String(choice)} style={SETTINGS_ROW}>
                <input
                  type="radio"
                  name="qf-finst-tint"
                  checked={choice === tintAt}
                  onChange={() => onTintAt(choice)}
                />
                {tintLabel(choice)}
              </label>
            ))}
          </fieldset>
          {hasTtm ? (
            <label style={{ ...SETTINGS_ROW, ...SETTINGS_RULED }}>
              <input type="checkbox" checked={showTtm} onChange={(event) => onShowTtm(event.target.checked)} />
              Show TTM
            </label>
          ) : null}
          <label style={{ ...SETTINGS_ROW, ...SETTINGS_RULED }}>
            Columns
            <select
              aria-label="Columns"
              value={columns === null ? "auto" : String(columns)}
              onChange={(event) => onColumns(event.target.value === "auto" ? null : Number(event.target.value))}
              style={SETTINGS_SELECT}
            >
              <option value="auto">Auto</option>
              {COLUMN_CHOICES.map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}
    </>
  );
}

/**
 * A % chg cell's background, or null. Green or red by whether the growth is
 * **welcome** — `YearRow`'s rule, so a metric that wants to fall reads the
 * other way — and none for a metric with no good direction.
 */
export function growthTint(
  value: number,
  direction: string | undefined,
  at: number | null = GROWTH_TINT,
): string | null {
  if (at === null || Math.abs(value) < at) return null;
  if (direction !== "up" && direction !== "down") return null;
  const welcome = direction === "down" ? value < 0 : value > 0;
  return `color-mix(in srgb, ${TONE_COLOUR[welcome ? "good" : "bad"]} 18%, transparent)`;
}

/**
 * The row's micro chart: one bar per column, **from zero**, so a bar's height
 * is the figure's size and not its distance from the row's low. A negative
 * value hangs below the line in red. Missing periods leave a gap rather than
 * closing up, so the bars stay under the columns they stand for.
 */
export function TrendBars({
  values,
  width: total = TREND_W,
}: {
  values: readonly (number | null)[];
  width?: number;
}): ReactElement | null {
  const finite = values.filter((v): v is number => v !== null);
  if (finite.length < 2) return null;
  const max = Math.max(0, ...finite);
  const min = Math.min(0, ...finite);
  const span = max - min || 1;
  const gap = 1;
  const width = (total - gap * (values.length - 1)) / values.length;
  const zero = (TREND_H * max) / span;
  return (
    <svg width={total} height={TREND_H} aria-hidden="true" style={{ display: "block" }}>
      {values.map((v, i) => {
        if (v === null) return null;
        const h = Math.max(1, (Math.abs(v) / span) * TREND_H);
        return (
          <rect
            key={i}
            data-trend-bar
            x={i * (width + gap)}
            y={v >= 0 ? zero - h : zero}
            width={width}
            height={h}
            fill={v < 0 ? TONE_COLOUR.bad : "var(--qf-text-muted)"}
          />
        );
      })}
      {min < 0 ? <line x1={0} x2={total} y1={zero} y2={zero} stroke="var(--qf-border)" strokeWidth={0.5} /> : null}
    </svg>
  );
}

/**
 * The column order of the full table (owner, 2026-10-15): **TTM first, after
 * Trend**, then the periods oldest to newest from `from`. Pinned there it is
 * never the column a narrow box drops, and it is the one figure read first.
 */
export function periodOrder(
  years: readonly { label: string }[],
  from: number,
  showTtm = true,
): number[] {
  const ttm = years.at(-1)?.label === "TTM" ? years.length - 1 : -1;
  const periods = years.map((_, i) => i).filter((i) => i !== ttm && i >= from);
  return ttm >= 0 && showTtm ? [ttm, ...periods] : periods;
}

/**
 * The values a row's micro chart draws: every period, less TTM where it does
 * not belong — hidden by the toggle, or beside quarters (four times their size)
 * — and less the estimate column, since the chart is of what was reported.
 */
export function trendValues(
  years: readonly { label: string; estimate?: unknown }[],
  values: readonly (number | null)[],
  grain: "years" | "quarters",
  showTtm: boolean,
): (number | null)[] {
  const dropTtm = grain === "quarters" || !showTtm;
  return values.filter((_, i) => !(dropTtm && years[i]?.label === "TTM") && years[i]?.estimate !== true);
}

/**
 * How many of the oldest periods to leave out so the table fits its box
 * (owner, 2026-10-15): **the newest period is always on screen** and there is
 * no sideways scroll, because a scrolled table hides the labels and the
 * reader lands on the oldest year rather than the one they came for.
 *
 * Measured rather than estimated — a column's width depends on its figures
 * (`-113.00M` against `0.12`) — so it starts from every period and drops one at
 * a time while the box overflows. Layout effects run before paint, so the
 * narrowing is never seen. A new width, symbol or grain starts over.
 *
 * jsdom reports no widths, so tests see every period.
 */
function useFitColumns(total: number, key: string): [React.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [from, setFrom] = useState(0);
  useEffect(() => {
    const box = ref.current;
    if (!box || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setWidth(box.clientWidth));
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  useLayoutEffect(() => setFrom(0), [total, width, key]);
  useLayoutEffect(() => {
    const box = ref.current;
    // One period at the least: TTM is pinned beside it, so there is still a pair.
    if (box && box.scrollWidth > box.clientWidth + 1 && from < total - 1) setFrom(from + 1);
  });
  return [ref, Math.min(from, Math.max(0, total - 1))];
}

function numberIn(raw: unknown): number | null {
  return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
}

function FinFold({ title, open: initial, children }: { title: string; open: boolean; children: React.ReactNode }): ReactElement {
  const [open, setOpen] = useState(initial);
  return (
    <div>
      <button type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} style={FIN_FOLD}>
        <span style={{ width: 14 }}>{open ? "▾" : "▸"}</span>
        {title}
      </button>
      {open && <div style={{ display: "flex", flexDirection: "column", gap: 5, padding: "2px 0 6px" }}>{children}</div>}
    </div>
  );
}

/** The phone card's micro chart: narrower than the table's, for 390px. */
const FIN_TREND_W = 48;

const FIN_CARD: React.CSSProperties = { padding: 12, display: "flex", flexDirection: "column", gap: 8, fontSize: 14, color: "var(--qf-text)" };
const FIN_FOLD: React.CSSProperties = { display: "flex", alignItems: "center", gap: 4, width: "100%", minHeight: 40, padding: 0, border: 0, background: "none", font: "inherit", fontWeight: 600, color: "var(--qf-text)", textAlign: "left", cursor: "pointer" };
const FIN_STEP: React.CSSProperties = { font: "inherit", fontSize: 12, padding: "6px 10px", borderRadius: 6, border: "1px solid var(--qf-border)", background: "var(--qf-surface)", color: "var(--qf-text-secondary)" };

/** When that year's figures became public, and whether anybody filed them. */
/**
 * Fin St's two column sets, each with its estimate column where there is one —
 * the full table and the phone card read the same pair, so they cannot differ.
 */
function finStColumns(data: SymbolFundamentals): { annual: AnnualRow[]; quarterly: AnnualRow[] } {
  const annual = data.annual ?? [];
  const quarterly = quarterColumns(data.periods ?? [], data.earnings ?? [], annual.at(-1));
  const metrics = { ...data.depth, ...data.metrics };
  const quarter = quarterEstimate(quarterly, metrics, data.earnings ?? []);
  return {
    annual: withEstimate(annual, yearEstimate(annual, quarterly, quarter)),
    quarterly: withEstimate(quarterly, quarter),
  };
}

function stampOfYear(year: SymbolFundamentals["annual"][number]): string {
  const on = year.availableOn ?? year.periodEnd;
  if (!on) return year.label;
  return year.pit === false ? `${on} (est.)` : String(on);
}

/**
 * The full panel: the QuickView band, then every metric that answered.
 *
 * **No lens chips.** The panel used to open on a row of them — All, Value,
 * Growth, Portfolio, Expectations — each re-cutting the same categories for a
 * different kind of reader. They went the day the band above landed, and the
 * reason is that the band already *is* the editorial choice: revenue and
 * earnings first, then quality, then what the market pays, then standing. A
 * reader who has that does not then want to be asked which kind of investor
 * they are before seeing the rest, and a reader who wants the rest wants all of
 * it. Two ways to answer one question is one too many.
 *
 * The server still publishes `lenses`, because the category groupings behind
 * them are real and a compare board may yet want them. Nothing here reads it.
 */
function FullFundamentals({
  data,
  chartGroupId,
}: {
  data: SymbolFundamentals;
  chartGroupId?: string | undefined;
}): ReactElement {
  return (
    <div style={SCROLL}>
      <FundamentalsQuickView data={data} chartGroupId={chartGroupId} />
    </div>
  );
}

/**
 * One metric as text, using the unit and precision the server declared.
 *
 * Server-declared rather than guessed from the value: a ratio of 0.28 and a
 * percentage of 0.28 are the same float and read as `0.28` and `28%`. The metric
 * table is the only thing that knows which, which is why it travels with the
 * numbers.
 */
function formatMetric(
  value: number | string | boolean | null | undefined,
  metric: FundamentalMetric | undefined,
): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "yes" : "no";
  // Before the string branch: a date arrives as `2026-10-29` and reads as
  // `29-Oct`. The year is noise on a date three months out, and dropping it is
  // what lets the row sit beside a countdown without wrapping.
  if (metric?.unit === "date") return formatDayMonth(value);
  if (typeof value === "string") return value;

  const precision = metric?.precision ?? 2;
  switch (metric?.unit) {
    case "percent":
      return formatPercent(value);
    case "money":
      return Math.abs(value) >= 1_000_000 ? formatCompact(value) : value.toFixed(precision);
    case "count":
      return value.toFixed(0);
    case "years":
      return `${value.toFixed(1)}y`;
    default:
      return value.toFixed(precision);
  }
}

/** `18.3 – 41.7`, or a dash when either end is missing — half a range is not one. */
function formatRange(
  [low, high]: readonly [string, string],
  value: (id: string) => number | string | boolean | null | undefined,
  by: Map<string, FundamentalMetric>,
): string {
  const lo = value(low);
  const hi = value(high);
  if (lo === null || lo === undefined || hi === null || hi === undefined) return "—";
  return `${formatMetric(lo, by.get(low))} – ${formatMetric(hi, by.get(high))}`;
}

/** `2026-10-29` -> `29-Oct`. Anything unparseable comes back untouched. */
function formatDayMonth(value: number | string): string {
  const parsed = new Date(typeof value === "number" ? value : `${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return String(value);
  const day = String(parsed.getDate()).padStart(2, "0");
  const month = parsed.toLocaleString("en-GB", { month: "short" });
  return `${day}-${month}`;
}

function formatPercent(fraction: number): string {
  const digits = fraction === 0 || Math.abs(fraction) >= 0.1 ? 1 : 2;
  return `${(fraction * 100).toFixed(digits)}%`;
}

function formatCompact(value: number): string {
  const abs = Math.abs(value);
  const [scale, suffix] =
    abs >= 1e12 ? [1e12, "T"] : abs >= 1e9 ? [1e9, "B"] : abs >= 1e6 ? [1e6, "M"] : [1, ""];
  return `${(value / scale).toFixed(2)}${suffix}`;
}

/**
 * The one view that is written rather than fetched.
 *
 * Every other tab is a number with a label. This one asks QueVee to read them
 * together and take a position — which is the only part of this panel a query
 * could not answer.
 *
 * **It costs a model call**, so it is cached per symbol and per question in
 * `lib/symbol-summary.ts` and re-run only from the control. Switching tab away
 * and back is free; changing the interval or adding an indicator is a different
 * question and earns a new answer.
 *
 * **The chart is what the browser contributes.** `chart()` reaches the surface
 * this panel was opened for and returns the interval, the indicators the reader
 * chose with their latest values, and a PNG when one can be captured. Everything
 * else — the profile, the fundamentals, the news — QueVee fetches itself, so an
 * analyst's inputs do not depend on who asked.
 */
function SummaryView({ symbol, compact, chartGroupId }: SymbolViewProps): ReactElement {
  const { thinking, elapsed, result, rerun, pick, picking } = useSummary(symbol, chartGroupId);

  if (picking) {
    return <Empty title={SUMMARY_LABEL} note="Loading that report…" compact={compact} quevee="thinking" />;
  }

  if (thinking || !result) {
    // **Named, and counting.** Measured: fetching the company takes under a
    // second and the model then writes about a thousand tokens at ~58 a second,
    // so this is a ~20 second wait however it is dressed. What a count buys is
    // the difference between a wait and a hang — and the stages are real, so a
    // reader who sees it stop on one knows which half failed.
    const stage =
      elapsed < 2
        ? "Reading the filings, the chart and the news"
        : "Writing the note";
    return (
      <Empty
        title={SUMMARY_LABEL}
        note={`${stage}… ${elapsed}s`}
        compact={compact}
        quevee="thinking"
      />
    );
  }

  if (!result.ok) {
    return <Empty title={SUMMARY_LABEL} note={result.error.message} compact={compact} />;
  }

  const note = result.summary;
  const tone = VERDICT_TONE[note.verdict];

  // The PIP gets the verdict and the thesis. A full analyst note in a 170px
  // corner of a chart is a paragraph nobody reads, and the panel is one click
  // away.
  if (compact) {
    return (
      <div style={PIP_PROFILE}>
        <div style={SUMMARY_HEAD}>
          <QueVeeMark size={18} title={QUEVEE_TOOLTIP} style={{ alignSelf: "center" }} />
          <span style={{ ...SUMMARY_VERDICT, color: tone ? TONE_COLOUR[tone] : undefined }}>
            {note.verdict}
          </span>
          <span style={SUMMARY_CONFIDENCE}>{Math.round(note.confidence * 100)}% confidence</span>
        </div>
        <p style={PIP_DESC} title={note.thesis}>
          {note.thesis}
        </p>
      </div>
    );
  }

  return (
    <div style={SCROLL}>
      <div style={SUMMARY_HEAD}>
        <QueVeeMark size={24} title={QUEVEE_TOOLTIP} playOnClick style={{ alignSelf: "center" }} />
        <span style={{ ...SUMMARY_VERDICT, color: tone ? TONE_COLOUR[tone] : undefined }}>
          {note.verdict}
        </span>
        <span style={SUMMARY_CONFIDENCE}>{Math.round(note.confidence * 100)}% confidence</span>
      </div>
      {/* The latest note is shown at any age, with its date; every past one is
          in the list, free to open. Refresh writes a new shared note — one
          model call, charged to whoever presses it — so a member waits until
          the latest is 15 minutes old (docs/features/summary.md). */}
      <div style={SUMMARY_ASOF_ROW}>
        {result.asOf && result.history && result.history.length > 1 ? (
          <label style={SUMMARY_FOOT_INLINE}>
            As of{" "}
            <select
              style={SUMMARY_ASOF_SELECT}
              value={result.asOf}
              onChange={(event) => {
                const chosen = event.target.value;
                pick(chosen === result.history?.[0]?.writtenAt ? undefined : chosen);
              }}
            >
              {result.history.map((entry, index) => (
                <option key={entry.writtenAt} value={entry.writtenAt}>
                  {formatAsOf(entry.writtenAt)}
                  {entry.verdict ? ` · ${entry.verdict}` : ""}
                  {index === 0 ? " (latest)" : ""}
                </option>
              ))}
            </select>
          </label>
        ) : result.asOf ? (
          <span style={SUMMARY_FOOT_INLINE}>As of {formatAsOf(result.asOf)}</span>
        ) : null}
        {result.canRefresh ? (
          <button
            type="button"
            style={SUMMARY_RERUN}
            onClick={() => rerun(true)}
            title="Write a new note now — one model call, charged to you"
          >
            ↻ Refresh
          </button>
        ) : result.refreshAt ? (
          <button
            type="button"
            style={SUMMARY_RERUN}
            disabled
            title="The latest note is under 15 minutes old"
          >
            ↻ Refresh at {formatAsOf(result.refreshAt)}
          </button>
        ) : null}
      </div>

      <p style={SUMMARY_THESIS}>{note.thesis}</p>

      <SummaryList title="What the numbers say" items={note.fundamentals} />
      <SummaryList title="What the chart says" items={note.technicals} />
      <SummaryList title="The case for" items={note.bull} tone="good" />
      <SummaryList title="The case against" items={note.bear} tone="bad" />

      {/* The self-critique the note wrote *before* its verdict — shown so a
          reader can judge whether it argued against itself honestly. */}
      {note.counterCase ? (
        <>
          <div style={CATEGORY}>Counter-case</div>
          <p style={SUMMARY_LINE}>{note.counterCase}</p>
        </>
      ) : null}

      <div style={CATEGORY}>What would change it</div>
      <p style={SUMMARY_LINE}>{note.watch}</p>

      <div style={CATEGORY}>Biggest risk</div>
      <p style={SUMMARY_LINE}>{note.risk}</p>

      {/* Not decoration. This is what lets a reader tell "the filings say
          nothing about this" from "nobody looked" — the distinction a
          confident-sounding summary destroys. */}
      {note.missing.length > 0 ? (
        <>
          <div style={CATEGORY}>Not available to read</div>
          {note.missing.map((entry) => (
            <p key={entry} style={SUMMARY_MISSING}>
              {entry}
            </p>
          ))}
        </>
      ) : null}

      {/* No longer mentions the picture. One is not sent — the chart travels as
          the visible closes and the indicator readings, which a measured
          comparison showed produce the same technical reading — so "without a
          chart image" would report the design as a shortfall on every note. */}
      {result.personal ? <PersonalPartView part={result.personal} /> : null}

      {(result.showCost ?? result.canRefresh) ? (
        <>
          <UsageLine label="common" usage={result.usage} cached={result.cached} />
          {result.personal && "points" in result.personal ? (
            <UsageLine label="yours" usage={result.personal.usage} cached={false} />
          ) : null}
        </>
      ) : null}

      <p style={SUMMARY_FOOT}>
        Written by QueVee from the fundamentals, the profile, recent coverage and a
        standard indicator set; the section on your indicators reads your chart. A
        judgement, not advice.
      </p>
    </div>
  );
}

/** "14:05 PDT" — the browser's own clock, with its zone so a shared screen reads right. */
function formatAsOf(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  const time = when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZoneName: "short" });
  // Notes are kept and shown at any age now, so anything not from today needs
  // its date — "09:12 PDT" alone would read as this morning.
  if (when.toDateString() === new Date().toDateString()) return time;
  return `${when.toLocaleDateString([], { month: "short", day: "numeric" })}, ${time}`;
}

const AGREEMENT_TEXT = {
  agrees: "agree with the verdict",
  mixed: "are mixed against the verdict",
  disagrees: "disagree with the verdict",
} as const;

/** What the reader's own indicators add — separate from the shared note, never edited into it. */
function PersonalPartView({ part }: { part: PersonalPart }): ReactElement {
  if ("error" in part) {
    return (
      <>
        <div style={CATEGORY}>Your indicators</div>
        <p style={SUMMARY_MISSING}>Could not read them this time: {part.error}</p>
      </>
    );
  }
  return (
    <>
      <div style={CATEGORY}>Your indicators · {AGREEMENT_TEXT[part.agrees]}</div>
      {part.points.map((point) => (
        <div key={point} style={SUMMARY_ITEM}>
          <span style={SUMMARY_BULLET}>•</span>
          <span>{point}</span>
        </div>
      ))}
    </>
  );
}

/**
 * Tokens and the billed price of one part — for everyone who can open a Summary.
 * Never the model: quevee no longer sends it (docs/features/summary.md, "Cost").
 */
function UsageLine({
  label,
  usage,
  cached,
}: {
  label: string;
  usage: SummaryUsage | null | undefined;
  cached: boolean | undefined;
}): ReactElement | null {
  if (!usage) return null;
  const tokens = `${usage.inputTokens.toLocaleString()} in / ${usage.outputTokens.toLocaleString()} out`;
  const calls = usage.calls > 1 ? ` · ${usage.calls} calls` : "";
  const cost =
    typeof usage.costUsd === "number" ? ` · ≈ $${usage.costUsd.toFixed(3)}` : "";
  return (
    <p style={SUMMARY_FOOT}>
      {label}: {tokens}
      {calls}
      {cost}
      {cached ? " (cached — no cost now)" : ""}
    </p>
  );
}

function SummaryList({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone?: "good" | "bad";
}): ReactElement | null {
  if (items.length === 0) return null;
  return (
    <>
      <div style={CATEGORY}>{title}</div>
      {items.map((item) => (
        <div key={item} style={SUMMARY_ITEM}>
          <span style={{ ...SUMMARY_BULLET, ...(tone ? { color: TONE_COLOUR[tone] } : {}) }}>
            •
          </span>
          <span>{item}</span>
        </div>
      ))}
    </>
  );
}

/**
 * Recent coverage, and the company's own releases below it.
 *
 * **Two sections, not one list.** Everything under "Coverage" is somebody
 * writing *about* the company; everything under "Releases" is the company's own
 * words. Putting a Motley Fool opinion piece and an 8-K in one list under one
 * heading would lose the distinction that matters most — which is also why they
 * come from two different vendors rather than one with a fallback.
 *
 * Each headline carries its sentiment as a coloured dot. The colour is the
 * theme's own semantic tokens, so a positive read is the same green as a rising
 * price everywhere else in the app.
 */
function NewsView({ symbol, compact }: SymbolViewProps): ReactElement {
  const data = useNews(symbol, !compact);

  if (!data) return <Empty title="News" note="loading…" compact={compact} />;
  if (data.articles.length === 0 && data.releases.length === 0) {
    return <Empty title="News" note="nothing published for this symbol" compact={compact} />;
  }

  if (compact) return <CompactNews articles={data.articles} />;

  const articles = data.articles;
  return (
    <div style={SCROLL}>
      {articles.map((article) => (
        <Headline key={article.id} article={article} />
      ))}
      {data.releases.length > 0 && (
        <>
          <div style={CATEGORY}>Releases</div>
          {data.releases.map((release) => (
            <a
              key={release.url}
              href={release.url}
              target="_blank"
              rel="noreferrer noopener"
              style={LINK}
              title={release.excerpt}
            >
              <span style={FIELD_K}>{stampOfDate(release.publishedUtc)}</span>
              <span style={FIELD_V}>{release.title}</span>
            </a>
          ))}
        </>
      )}
    </div>
  );
}

/**
 * The sentiment mix over recent coverage, and why the vendor read it that way.
 *
 * **Counts, not a score.** The source says which of three buckets an article
 * falls in and nothing about how strongly, so averaging them onto one number
 * invents a precision it does not have. `net` is the one derived figure and
 * excludes neutrals from its denominator: a month of genuinely neutral coverage
 * and a month of no coverage are different things.
 *
 * The reasoning paragraphs are the point of the view. A sentiment label alone is
 * a number to trust or ignore; a label with its argument is one you can check.
 */
function SentimentView({ symbol, compact }: SymbolViewProps): ReactElement {
  const data = useNews(symbol, false);

  if (!data) return <Empty title="Sentiment" note="loading…" compact={compact} />;
  const { counts, total, opinionated, net } = data.sentiment;
  if (total === 0) {
    return <Empty title="Sentiment" note="no coverage to read" compact={compact} />;
  }

  const rows: [string, string][] = [
    ["Positive", String(counts.positive)],
    ["Neutral", String(counts.neutral)],
    ["Negative", String(counts.negative)],
    ["Net", net === null ? "—" : formatPercent(net)],
    ["Articles", `${total}, ${opinionated} with a view`],
  ];

  if (compact) {
    return (
      <div style={SCROLL_COMPACT}>
        {rows.map(([k, v]) => (
          <Field key={k} label={k} value={v} compact />
        ))}
      </div>
    );
  }

  // Only the articles that took a side, and each with its argument. A neutral
  // reading with the reasoning "no specific analysis" is noise in a list whose
  // purpose is to show what the view rests on.
  const opinions = data.articles.filter(
    (a) => a.sentiment === "positive" || a.sentiment === "negative",
  );

  return (
    <div style={SCROLL}>
      {rows.map(([k, v]) => (
        <Field key={k} label={k} value={v} />
      ))}
      {opinions.length > 0 && <div style={CATEGORY}>What the reading rests on</div>}
      {opinions.map((article) => (
        <div key={article.id} style={{ padding: "3px 0" }}>
          <a
            href={article.url}
            target="_blank"
            rel="noreferrer noopener"
            style={{ ...LINK, padding: 0 }}
          >
            <Dot sentiment={article.sentiment} />
            <span style={FIELD_V}>{article.title}</span>
          </a>
          {article.reasoning && <div style={REASONING}>{article.reasoning}</div>}
        </div>
      ))}
    </div>
  );
}

/**
 * As many recent headlines as the box holds, and not one more.
 *
 * **Measured rather than assumed.** A PIP is `PIP_FRACTIONS` of the price pane,
 * so its height is whatever the chart and the size setting make it — six
 * headlines fit at size l and overflow at size s, and a fixed count is
 * therefore wrong at one end or the other whichever number is chosen. A
 * `ResizeObserver` reads the box and divides.
 *
 * **Which only works because every row is exactly one line.** A wrapping title
 * makes row height a function of the text, so the count could not be computed
 * from the height — and a headline half-visible at the fold is worse than one
 * that is absent, because it reads as a rendering fault rather than as a list
 * that ended. So titles truncate, the box does not scroll, and what is on
 * screen is the whole of what is being shown.
 */
function CompactNews({ articles }: { articles: NewsArticle[] }): ReactElement {
  const [host, base] = usePipScale();
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const node = host.current;
    if (!node) return;
    const measure = () => setHeight(node.clientHeight);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [host]);

  // The row height scales with the type, so the count has to be recomputed from
  // both — a bigger PIP shows *larger* headlines, not merely more of them.
  const rowHeight = base * PIP_NEWS_ROW_EM;
  const fits = Math.max(1, Math.floor((height - 4) / rowHeight));

  return (
    <div ref={host} style={{ ...PIP_NEWS, fontSize: base }}>
      {articles.slice(0, fits).map((article) => (
        <a
          key={article.id}
          href={article.url}
          target="_blank"
          rel="noreferrer noopener"
          style={{ ...PIP_HEADLINE, height: `${PIP_NEWS_ROW_EM}em` }}
          title={`${article.title}${article.publisher ? ` — ${article.publisher}` : ""}`}
        >
          <Dot sentiment={article.sentiment} />
          <span style={PIP_HEADLINE_DATE}>{stampOfDate(article.publishedUtc)}</span>
          <span style={PIP_HEADLINE_TEXT}>{article.title}</span>
        </a>
      ))}
    </div>
  );
}

function Headline({ article }: { article: NewsArticle }): ReactElement {
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noreferrer noopener"
      style={LINK}
      title={article.reasoning ?? article.description ?? article.title}
    >
      <Dot sentiment={article.sentiment} />
      <span style={FIELD_V}>{article.title}</span>
      <span style={FIELD_K}>
        {[article.publisher, stampOfDate(article.publishedUtc)].filter(Boolean).join(" · ")}
      </span>
    </a>
  );
}

/** Sentiment as a dot, in the same semantic tokens a rising price uses. */
function Dot({ sentiment }: { sentiment: string | null }): ReactElement {
  const color =
    sentiment === "positive"
      ? "var(--qf-positive, #2e7d32)"
      : sentiment === "negative"
        ? "var(--qf-negative, #c62828)"
        : "var(--qf-text-secondary)";
  return (
    <span
      aria-label={sentiment ?? "no reading"}
      title={sentiment ?? "no reading"}
      style={{
        flex: "0 0 auto",
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: color,
        // A neutral dot is an outline: three filled dots in three greys are
        // harder to tell apart than two filled and one hollow.
        opacity: sentiment === "neutral" ? 0.45 : 1,
      }}
    />
  );
}

/** The date alone. A panel row has no width for a timestamp. */
function stampOfDate(value: string): string {
  return (value || "").slice(0, 10);
}

/**
 * What a view shows when it has nothing to show.
 *
 * It names the view and says what is missing, because the two failures it has to
 * be told apart from are "the fetch returned nothing" and "the panel is broken",
 * and a blank box is indistinguishable from both.
 */
function Empty({
  title,
  note,
  compact,
  quevee,
}: {
  title: string;
  note: string;
  compact?: boolean;
  /**
   * QueVee is writing this view: its mark, large, to the left of both lines and
   * looping (owner, 2026-11-02 — big enough that the leaves' swing reads).
   */
  quevee?: "thinking";
}): ReactElement {
  const text = (
    <>
      <b style={{ color: "var(--qf-text-secondary)", fontWeight: 600 }}>{title}</b>
      <span>{note}</span>
    </>
  );
  if (quevee) {
    return (
      <div style={compact ? EMPTY_COMPACT : EMPTY}>
        <div data-quevee-wait style={{ display: "flex", alignItems: "center", gap: compact ? 6 : 12 }}>
          <QueVeeMark size={compact ? 28 : 52} title={QUEVEE_TOOLTIP} thinking />
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left", gap: compact ? 1 : 3 }}>
            {text}
          </div>
        </div>
      </div>
    );
  }
  return <div style={compact ? EMPTY_COMPACT : EMPTY}>{text}</div>;
}

function Field({
  label,
  value,
  compact,
}: {
  label: string;
  value: string;
  compact?: boolean;
}): ReactElement {
  return (
    <div style={compact ? FIELD_COMPACT : FIELD}>
      <span style={compact ? FIELD_K_COMPACT : FIELD_K}>{label}</span>
      <span style={compact ? FIELD_V_COMPACT : FIELD_V} title={value}>
        {value}
      </span>
    </div>
  );
}

const LINK: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 6,
  padding: "2px 0",
  color: "inherit",
  textDecoration: "none",
};

const REASONING: React.CSSProperties = {
  fontSize: "0.68rem",
  lineHeight: 1.35,
  color: "var(--qf-text-secondary)",
  paddingLeft: 12,
};

/**
 * The year table scrolls sideways inside its own box, never the panel.
 *
 * Six columns of numbers do not fit a narrow dock, and a panel that scrolls
 * horizontally takes the fact grid and the lens list with it. Bounding the
 * scroll to the table is what lets the same markup work docked at 300px and
 * detached on a monitor.
 */
const YEAR_SCROLL: React.CSSProperties = { overflowX: "auto", paddingBottom: 2 };

const YEAR_TABLE: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
  minWidth: 300,
  fontVariantNumeric: "tabular-nums",
};

const YEAR_HEAD: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 600,
  textAlign: "right",
  padding: "0 5px 3px",
  color: "var(--qf-text-muted)",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};

const YEAR_HEAD_TREND: React.CSSProperties = { ...YEAR_HEAD, textAlign: "left", padding: "0 6px 3px" };

/** The current column is the one being read; the years are the context. */
const YEAR_HEAD_NOW: React.CSSProperties = {
  ...YEAR_HEAD,
  color: "var(--qf-text)",
  fontWeight: 700,
  borderLeft: "1px solid var(--qf-border)",
};

/** TTM beside Trend: the rule moves to its right, between now and the history. */
const YEAR_HEAD_TTM_FIRST: React.CSSProperties = {
  ...YEAR_HEAD_NOW,
  borderLeft: "none",
  borderRight: "1px solid var(--qf-border)",
};

const YEAR_HEAD_K: React.CSSProperties = {
  ...YEAR_HEAD,
  textAlign: "left",
  width: "34%",
  minWidth: 108,
  borderRight: "1px solid var(--qf-border)",
};

const GRAIN_GROUP: React.CSSProperties = {
  display: "inline-flex",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  overflow: "hidden",
};

const GRAIN_OFF: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  font: "inherit",
  fontSize: 11,
  padding: "1px 7px",
  cursor: "pointer",
};

/** The label column's header: YR / QTR and the settings ⚙ on one line. */
const HEAD_CONTROLS: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 6 };

/** 32px is the smallest a thumb hits without a second try. */
const TOUCH_CONTROL: React.CSSProperties = { minHeight: 32, fontSize: 13, padding: "0 10px" };

const SETTINGS_BUTTON: React.CSSProperties = {
  font: "inherit",
  fontSize: 12,
  lineHeight: 1,
  padding: "1px 5px",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};

const SETTINGS_MENU: React.CSSProperties = {
  position: "fixed",
  zIndex: 1000,
  minWidth: 150,
  padding: "6px 10px",
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 4px 14px rgba(0, 0, 0, 0.18)",
  fontSize: 12,
  fontWeight: 400,
  textAlign: "left",
  color: "var(--qf-text)",
};

const SETTINGS_GROUP: React.CSSProperties = { border: 0, margin: 0, padding: 0 };

const SETTINGS_LEGEND: React.CSSProperties = { padding: 0, marginBottom: 2, color: "var(--qf-text-muted)" };

const SETTINGS_ROW: React.CSSProperties = { display: "flex", alignItems: "center", gap: 6, minHeight: 24, cursor: "pointer" };

const SETTINGS_RULED: React.CSSProperties = { borderTop: "1px solid var(--qf-border)", marginTop: 4, paddingTop: 4 };

const SETTINGS_SELECT: React.CSSProperties = { marginLeft: "auto", font: "inherit" };

const GRAIN_ON: React.CSSProperties = {
  ...GRAIN_OFF,
  background: "var(--qf-selection)",
  color: "var(--qf-text)",
  fontWeight: 600,
};

const YEAR_GROUP: React.CSSProperties = {
  fontSize: 9.5,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  textAlign: "left",
  fontWeight: 600,
  color: "var(--qf-text-muted)",
  padding: "7px 0 2px",
};

/**
 * Every group after the first opens with a rule above it.
 *
 * A heading alone stops being enough separation once the rows either side of it
 * are striped: the stripe is what carries the eye across six columns, and it
 * also makes an unruled heading read as one more row of the group above.
 */
const YEAR_GROUP_RULED: React.CSSProperties = {
  ...YEAR_GROUP,
  paddingTop: 10,
  borderTop: "1px solid var(--qf-border)",
};

const YEAR_K: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 400,
  textAlign: "left",
  padding: "2px 8px 2px 0",
  color: "var(--qf-text)",
  whiteSpace: "nowrap",
  borderRight: "1px solid var(--qf-border)",
};

/** A percentage change belongs under the figure it is a change in. */
const YEAR_K_INDENT: React.CSSProperties = {
  ...YEAR_K,
  paddingLeft: 10,
  fontSize: 10.5,
  color: "var(--qf-text-muted)",
};

const YEAR_V: React.CSSProperties = {
  fontSize: 11,
  textAlign: "right",
  padding: "2px 5px",
  color: "var(--qf-text-secondary)",
  whiteSpace: "nowrap",
};

/**
 * The current column, and the seam in front of it.
 *
 * Everything to its left is history and everything on it is now — the one
 * boundary in this table a reader actually navigates by, so it earns a line
 * rather than only a heavier weight.
 */
const YEAR_V_NOW: React.CSSProperties = {
  ...YEAR_V,
  fontSize: 11.5,
  fontWeight: 600,
  color: "var(--qf-text)",
  borderLeft: "1px solid var(--qf-border)",
};

const YEAR_V_TTM_FIRST: React.CSSProperties = {
  ...YEAR_V_NOW,
  borderLeft: "none",
  borderRight: "1px solid var(--qf-border)",
};

/**
 * The estimate column, boxed in yellow (owner, 2026-10-28): `warn`, the token
 * the chart draws a forward projection in, because both say "not reported yet".
 */
const EST_LINE = "1px solid var(--qf-warn)";
const EST_SIDES: React.CSSProperties = { borderLeft: EST_LINE, borderRight: EST_LINE };
const YEAR_V_EST: React.CSSProperties = { ...YEAR_V, ...EST_SIDES };
const YEAR_HEAD_EST: React.CSSProperties = { ...YEAR_HEAD, ...EST_SIDES, borderTop: EST_LINE };
const EST_MARK: React.CSSProperties = { color: "var(--qf-warn)", fontWeight: 400 };
const EST_DATE: React.CSSProperties = {
  ...EST_SIDES,
  ...EST_MARK,
  fontSize: 10.5,
  textAlign: "right",
  padding: "7px 5px 2px",
  whiteSpace: "nowrap",
};

const MARK: React.CSSProperties = { fontSize: 7, marginLeft: 2, verticalAlign: 1 };

const TREND_W = 64;
const TREND_H = 14;

/** The micro chart's cell: between the label's rule and the first period. */
const YEAR_TREND: React.CSSProperties = { padding: "2px 6px", verticalAlign: "middle" };

/** The marker on a year whose publication date was assumed, not filed. */
const ESTIMATED: React.CSSProperties = { color: "var(--qf-text-muted)", fontWeight: 400 };

/**
 * The fact table scrolls in its own box, never taking the panel with it.
 *
 * Three label-and-value pairs is six columns, which does not fit a narrow dock —
 * and a panel that scrolled sideways would drag the year table above it along
 * too. Bounding the scroll to the table is what lets one markup work docked and
 * detached.
 */
const FACT_SCROLL: React.CSSProperties = { overflowX: "auto", paddingTop: 8 };

const FACT_TABLE: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
  minWidth: 460,
  fontVariantNumeric: "tabular-nums",
};

const FACT_HEAD: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  textAlign: "left",
  padding: "0 8px 4px 12px",
  color: "var(--qf-text)",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};

/**
 * The rule between one question and the next.
 *
 * Three label-and-value pairs on a row is six columns, and with no seam the
 * value of one pair sits directly beside the label of the next — which reads as
 * a four-column table with two of its headings in the wrong place.
 */
const FACT_SEAM: React.CSSProperties = { borderLeft: "1px solid var(--qf-border)" };

/** The leftmost heading has no seam in front of it, so it needs no inset. */
const FACT_HEAD_FIRST: React.CSSProperties = { ...FACT_HEAD, paddingLeft: 0 };

const FACT_HEAD_V: React.CSSProperties = {
  ...FACT_HEAD,
  textAlign: "right",
  paddingRight: 16,
  color: "var(--qf-text-muted)",
  fontWeight: 600,
};

const FACT_K: React.CSSProperties = {
  fontSize: 11,
  textAlign: "left",
  padding: "3px 8px 3px 12px",
  color: "var(--qf-text-muted)",
  whiteSpace: "nowrap",
};

const FACT_V: React.CSSProperties = {
  fontSize: 11.5,
  fontWeight: 600,
  textAlign: "right",
  padding: "3px 16px 3px 0",
  color: "var(--qf-text)",
  whiteSpace: "nowrap",
};

/**
 * The profile header, description and link.
 *
 * A paragraph, not a field: a description is 400 characters and putting it in a
 * label-and-value row would give it a 92px gutter and eleven lines of a column
 * two words wide.
 */
const PROFILE_HEAD: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 8,
  flexWrap: "wrap",
  paddingBottom: 2,
};

const PROFILE_NAME: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 650,
  color: "var(--qf-text)",
};

const PROFILE_BADGE: React.CSSProperties = {
  fontSize: 9,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  padding: "1px 5px",
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  color: "var(--qf-text-muted)",
};

/**
 * A size up from the tables, because it is prose rather than a lookup.
 *
 * Everything else in this panel is scanned — a reader knows which row they want
 * before they look at it — and this is the one block that is actually *read*,
 * left to right, several sentences of it.
 */
const PROFILE_DESC: React.CSSProperties = {
  margin: "2px 0 7px",
  fontSize: 12.5,
  lineHeight: 1.6,
  color: "var(--qf-text-secondary)",
};

/**
 * A profile value, bounded.
 *
 * The fact table's own values are numbers and never need this; a company's
 * headquarters, exchange name or industry classification is prose and will
 * happily set the table's width to whatever the longest one happens to be.
 */
const PROFILE_V: React.CSSProperties = {
  ...FACT_V,
  maxWidth: 150,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/**
 * The description in a PIP: three lines, then an ellipsis.
 *
 * Clamped rather than scrolled, because a scrollable paragraph inside a panel
 * that is itself scrollable gives the reader two overlapping scroll regions and
 * no way to tell which one the wheel will move. Three lines is the lead
 * sentence, which is the part that says what the company does; the rest is in
 * the tooltip and in the panel.
 */
/**
 * The year table at PIP density.
 *
 * Same structure as the panel's and about two thirds the size: 9px figures, a
 * 62px label column, and no `min-width` at all, so three columns fit the
 * narrowest PIP without a sideways scrollbar.
 */
const PIP_NEWS: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  padding: "2px 6px",
  // Not `auto`: the whole point is that what is shown is what fits, and a
  // scrollbar would mean the measurement was wrong.
  overflow: "hidden",
};

/** Height comes from the caller, in `em`, so it tracks the measured type. */
const PIP_HEADLINE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 5,
  color: "inherit",
  textDecoration: "none",
};

const PIP_HEADLINE_DATE: React.CSSProperties = {
  flex: "0 0 auto",
  fontSize: "0.85em",
  color: "var(--qf-text-muted)",
  fontVariantNumeric: "tabular-nums",
};

const PIP_HEADLINE_TEXT: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: "0.95em",
  color: "var(--qf-text)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const PIP_YEAR_TABLE: React.CSSProperties = {
  borderCollapse: "collapse",
  width: "100%",
  marginBottom: 4,
  fontVariantNumeric: "tabular-nums",
};

const PIP_YEAR_HEAD: React.CSSProperties = {
  fontSize: "0.85em",
  fontWeight: 600,
  textAlign: "right",
  padding: "0 3px 2px",
  color: "var(--qf-text-muted)",
  borderBottom: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};

const PIP_YEAR_HEAD_NOW: React.CSSProperties = {
  ...PIP_YEAR_HEAD,
  color: "var(--qf-text)",
  fontWeight: 700,
  borderLeft: "1px solid var(--qf-border)",
};

const PIP_YEAR_HEAD_K: React.CSSProperties = {
  ...PIP_YEAR_HEAD,
  textAlign: "left",
  width: 62,
  borderRight: "1px solid var(--qf-border)",
};

const PIP_YEAR_K: React.CSSProperties = {
  fontSize: "0.9em",
  fontWeight: 400,
  textAlign: "left",
  padding: "1px 4px 1px 0",
  color: "var(--qf-text)",
  whiteSpace: "nowrap",
  borderRight: "1px solid var(--qf-border)",
};

const PIP_YEAR_K_INDENT: React.CSSProperties = {
  ...PIP_YEAR_K,
  paddingLeft: 7,
  fontSize: "0.85em",
  color: "var(--qf-text-muted)",
};

const PIP_YEAR_V: React.CSSProperties = {
  fontSize: "0.9em",
  textAlign: "right",
  padding: "1px 3px",
  color: "var(--qf-text-secondary)",
  whiteSpace: "nowrap",
};

const PIP_YEAR_V_NOW: React.CSSProperties = {
  ...PIP_YEAR_V,
  fontWeight: 600,
  color: "var(--qf-text)",
  borderLeft: "1px solid var(--qf-border)",
};

/**
 * The Profile PIP: a column, with the facts pinned low.
 *
 * The four values take a fixed two rows at the bottom and the description
 * takes everything above them, so a taller PIP shows more of the paragraph
 * rather than more empty space.
 */
const PIP_PROFILE: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  padding: "4px 6px 5px",
  display: "flex",
  flexDirection: "column",
  gap: 4,
  overflow: "hidden",
};

/**
 * The description, filling what is left.
 *
 * Cut rather than scrolled: a scrollable paragraph inside a panel that also
 * scrolls gives the reader two overlapping scroll regions and no way to tell
 * which one the wheel will move. `overflow: hidden` on a flex child ends it at
 * the last line that fits, whatever height the PIP happens to be, and the full
 * text is one hover away.
 */
const PIP_DESC: React.CSSProperties = {
  margin: 0,
  flex: "1 1 auto",
  minHeight: 0,
  overflow: "hidden",
  fontSize: "0.95em",
  lineHeight: 1.45,
  color: "var(--qf-text-secondary)",
};

/** Four values as two rows of two, label and value alternating. */
const PIP_PAIRS: React.CSSProperties = {
  flex: "0 0 auto",
  display: "grid",
  gridTemplateColumns: "auto 1fr auto 1fr",
  gap: "1px 5px",
  alignItems: "baseline",
  paddingTop: 3,
  borderTop: "1px solid var(--qf-border)",
};

const PIP_PAIR_K: React.CSSProperties = {
  fontSize: "0.85em",
  color: "var(--qf-text-muted)",
  whiteSpace: "nowrap",
};

const PIP_PAIR_V: React.CSSProperties = {
  fontSize: "0.95em",
  fontWeight: 600,
  color: "var(--qf-text)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const PROFILE_LINK: React.CSSProperties = {
  fontSize: 11,
  color: "var(--qf-accent)",
  textDecoration: "none",
};

/** A peer's ticker, which is a control rather than a label. */
const PEER_LINK: React.CSSProperties = {
  font: "inherit",
  fontSize: 11,
  fontWeight: 600,
  padding: 0,
  border: 0,
  background: "none",
  color: "var(--qf-accent)",
  cursor: "pointer",
  textAlign: "left",
};

/**
 * The peer table at PIP width.
 *
 * A lower `min-width` than the panel's, so four columns fit at 280px instead of
 * forcing a horizontal scroll inside a 170-pixel-tall box — which is the one
 * place a sideways scrollbar is genuinely unusable.
 */
const PEER_TABLE_COMPACT: React.CSSProperties = {
  ...FACT_TABLE,
  minWidth: 0,
  fontSize: "1em",
};

const PEER_HEAD_NAME: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 600,
  textAlign: "left",
  padding: "0 8px 4px 12px",
  color: "var(--qf-text-muted)",
  borderBottom: "1px solid var(--qf-border)",
  borderLeft: "1px solid var(--qf-border)",
  whiteSpace: "nowrap",
};

const PEER_NAME: React.CSSProperties = {
  fontSize: 11,
  textAlign: "left",
  padding: "3px 8px 3px 12px",
  color: "var(--qf-text-secondary)",
  borderLeft: "1px solid var(--qf-border)",
  maxWidth: 190,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

/**
 * The explanation box: one fixed height, whatever it happens to be saying.
 *
 * **A `min-height` was not enough, and the failure was worse than the one it
 * fixed.** One caveat is a line and the next is five, so the box grew and shrank
 * as the pointer crossed rows — which moves every row below it, and moving a row
 * out from under the pointer un-hovers it, which changes the text, which resizes
 * the box again. A flicker loop the reader drives without meaning to.
 *
 * So the height is fixed and the *text* scrolls inside it. The panel never
 * reflows, which means the row under the pointer stays under the pointer, which
 * is the only property that makes hovering a table usable at all.
 */
const HINT_BOX: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  marginTop: 10,
  padding: "7px 9px",
  height: 104,
  flex: "0 0 auto",
  overflowY: "auto",
  borderRadius: 4,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
};

const HINT_LABEL: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "var(--qf-text)",
};

const HINT_TEXT: React.CSSProperties = {
  fontSize: 11.5,
  lineHeight: 1.5,
  color: "var(--qf-text)",
};

/** The rule of thumb, which is the half worth having and the half that is long. */
const HINT_NOTE: React.CSSProperties = {
  fontSize: 11,
  lineHeight: 1.5,
  color: "var(--qf-text-secondary)",
};

const HINT_IDLE: React.CSSProperties = {
  fontSize: 11,
  color: "var(--qf-text-muted)",
  fontStyle: "italic",
};

/** The verdict strip: the stance, how sure, and the way to ask again. */
const SUMMARY_HEAD: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 8,
  paddingBottom: 4,
};

const SUMMARY_VERDICT: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 700,
  color: "var(--qf-text)",
};

const SUMMARY_CONFIDENCE: React.CSSProperties = {
  fontSize: 10.5,
  color: "var(--qf-text-muted)",
};

const SUMMARY_RERUN: React.CSSProperties = {
  font: "inherit",
  fontSize: 10.5,
  marginLeft: "auto",
  padding: "1px 7px",
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};

const SUMMARY_THESIS: React.CSSProperties = {
  margin: "0 0 4px",
  fontSize: 12.5,
  lineHeight: 1.6,
  color: "var(--qf-text)",
};

const SUMMARY_ITEM: React.CSSProperties = {
  display: "flex",
  gap: 6,
  padding: "2px 0",
  fontSize: 11.5,
  lineHeight: 1.5,
  color: "var(--qf-text-secondary)",
};

const SUMMARY_BULLET: React.CSSProperties = {
  flex: "0 0 auto",
  color: "var(--qf-text-muted)",
};

const SUMMARY_LINE: React.CSSProperties = {
  margin: "2px 0 0",
  fontSize: 11.5,
  lineHeight: 1.5,
  color: "var(--qf-text-secondary)",
};

const SUMMARY_MISSING: React.CSSProperties = {
  ...SUMMARY_LINE,
  color: "var(--qf-text-muted)",
  fontStyle: "italic",
};

/** Said once, at the end: this is a judgement and it is a machine's. */
const SUMMARY_FOOT: React.CSSProperties = {
  margin: "10px 0 0",
  paddingTop: 6,
  borderTop: "1px solid var(--qf-border)",
  fontSize: 10,
  lineHeight: 1.5,
  color: "var(--qf-text-muted)",
};

/** "As of [date ▾]" and Refresh, on one line under the verdict. */
const SUMMARY_ASOF_ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
  margin: "2px 0 6px",
};

const SUMMARY_FOOT_INLINE: React.CSSProperties = {
  fontSize: 10,
  color: "var(--qf-text-muted)",
};

const SUMMARY_ASOF_SELECT: React.CSSProperties = {
  font: "inherit",
  fontSize: 10.5,
  padding: "1px 4px",
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
  color: "var(--qf-text)",
  maxWidth: "100%",
};

const CATEGORY: React.CSSProperties = {
  fontSize: "0.62rem",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--qf-text-secondary)",
  padding: "6px 0 2px",
};

const SCROLL: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
};
const SCROLL_COMPACT: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  overflowY: "auto",
  padding: "5px 6px",
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const FIELD: React.CSSProperties = {
  display: "flex",
  gap: 10,
  alignItems: "baseline",
  padding: "4px 6px",
  borderRadius: 3,
};
const FIELD_COMPACT: React.CSSProperties = { display: "flex", gap: 6, alignItems: "baseline" };

const FIELD_K: React.CSSProperties = {
  flex: "0 0 92px",
  fontSize: 11,
  color: "var(--qf-text-muted)",
};
const FIELD_K_COMPACT: React.CSSProperties = {
  flex: "0 0 58px",
  fontSize: "0.9em",
  color: "var(--qf-text-muted)",
};

const FIELD_V: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  fontSize: 12.5,
  color: "var(--qf-text)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};
const FIELD_V_COMPACT: React.CSSProperties = { ...FIELD_V, fontSize: 10 };

const EMPTY: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 5,
  minHeight: 120,
  padding: 20,
  textAlign: "center",
  fontSize: 12,
  lineHeight: 1.55,
  color: "var(--qf-text-muted)",
  border: "1px dashed var(--qf-border)",
  borderRadius: 5,
};
const EMPTY_COMPACT: React.CSSProperties = {
  position: "absolute",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 3,
  padding: 8,
  textAlign: "center",
  fontSize: 9.5,
  lineHeight: 1.4,
  color: "var(--qf-text-muted)",
};
